import React, { useEffect, useMemo, useRef, useState } from 'react';
import './App.css';

const IS_LOCAL_RUNTIME = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const CONFIGURED_API_BASE_URL = (process.env.REACT_APP_API_URL || '').trim();
const SHOULD_IGNORE_LOCALHOST_ENV =
  !IS_LOCAL_RUNTIME &&
  /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/|$)/i.test(CONFIGURED_API_BASE_URL);

const API_BASE_URL = (
  SHOULD_IGNORE_LOCALHOST_ENV
    ? '/api'
    : (CONFIGURED_API_BASE_URL
      || (IS_LOCAL_RUNTIME
        ? 'http://localhost:3001/api'
        : '/api'))
).replace(/\/$/, '');
const TEAM_SOUND_FILE = 'goal-crowd-roaring_F_minor.wav';
const TEAM_SOUND_PATHS = [
  `${process.env.PUBLIC_URL || ''}/assets/sounds/${TEAM_SOUND_FILE}`,
  `/assets/sounds/${TEAM_SOUND_FILE}`,
  `assets/sounds/${TEAM_SOUND_FILE}`
].filter(Boolean);
const ROAR_VOLUME_STORAGE_KEY = 'rooarVolume';
const ROAR_TEAM_IDS_STORAGE_KEY = 'rooarTeamIds';
const ROAR_PANEL_STORAGE_KEY = 'rooarPanelOpen';
const TENANT_SLUG_STORAGE_KEY = 'currentTenantSlug';
const FRIENDS_SCOPE_KEY = 'all-matches';
const NEXT_MATCHES_LIMIT = 6;
const LANGUAGE_STORAGE_KEY = 'uiLanguage';
const LANGUAGE_FLAG = {
  de: '🇩🇪',
  en: '🇬🇧'
};
const VENUE_PLACEHOLDER_PATH = `${process.env.PUBLIC_URL || ''}/assets/venue-placeholder.svg`;
const GROUP_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'];
const WM_2026_PHASE_TEMPLATE = [
  { stage: 'Group Stage', teamsCount: 48, fixtureCount: 72 },
  { stage: 'Round of 32', teamsCount: 32, fixtureCount: 16 },
  { stage: 'Round of 16', teamsCount: 16, fixtureCount: 8 },
  { stage: 'Quarter-finals', teamsCount: 8, fixtureCount: 4 },
  { stage: 'Semi-finals', teamsCount: 4, fixtureCount: 2 },
  { stage: '3rd Place Final', teamsCount: 2, fixtureCount: 1 },
  { stage: 'Final', teamsCount: 2, fixtureCount: 1 }
];
const HOST_VENUES = [
  { name: 'MetLife Stadium', city: 'New York', country: 'USA', timeZone: 'America/New_York' },
  { name: 'Gillette Stadium', city: 'Boston', country: 'USA', timeZone: 'America/New_York' },
  { name: 'Lincoln Financial Field', city: 'Philadelphia', country: 'USA', timeZone: 'America/New_York' },
  { name: 'SoFi Stadium', city: 'Los Angeles', country: 'USA', timeZone: 'America/Los_Angeles' },
  { name: 'AT&T Stadium', city: 'Dallas', country: 'USA', timeZone: 'America/Chicago' },
  { name: 'NRG Stadium', city: 'Houston', country: 'USA', timeZone: 'America/Chicago' },
  { name: 'Arrowhead Stadium', city: 'Kansas City', country: 'USA', timeZone: 'America/Chicago' },
  { name: 'Hard Rock Stadium', city: 'Miami', country: 'USA', timeZone: 'America/New_York' },
  { name: 'Lumen Field', city: 'Seattle', country: 'USA', timeZone: 'America/Los_Angeles' },
  { name: 'Levis Stadium', city: 'San Francisco', country: 'USA', timeZone: 'America/Los_Angeles' },
  { name: 'BC Place', city: 'Vancouver', country: 'Canada', timeZone: 'America/Vancouver' },
  { name: 'BMO Field', city: 'Toronto', country: 'Canada', timeZone: 'America/Toronto' },
  { name: 'Estadio Azteca', city: 'Mexico City', country: 'Mexico', timeZone: 'America/Mexico_City' },
  { name: 'Estadio Akron', city: 'Guadalajara', country: 'Mexico', timeZone: 'America/Mexico_City' },
  { name: 'Estadio BBVA', city: 'Monterrey', country: 'Mexico', timeZone: 'America/Monterrey' },
  { name: 'Mercedes-Benz Stadium', city: 'Atlanta', country: 'USA', timeZone: 'America/New_York' }
];
const GROUP_KEY_VENUE_CITIES = {
  A: ['Mexico City', 'Atlanta', 'Guadalajara'],
  B: ['Toronto', 'Vancouver', 'Los Angeles'],
  C: ['New York', 'Boston', 'Philadelphia', 'Seattle'],
  D: ['Los Angeles', 'Seattle'],
  E: ['Toronto', 'Kansas City', 'Dallas'],
  F: ['Dallas', 'Houston', 'Kansas City', 'Monterrey'],
  G: ['Seattle', 'Los Angeles', 'Miami', 'Vancouver'],
  H: ['Atlanta', 'Miami', 'Kansas City'],
  I: ['New York', 'Philadelphia', 'Dallas'],
  J: ['Dallas', 'Los Angeles', 'San Francisco'],
  K: ['Houston', 'Mexico City', 'Kansas City'],
  L: ['Dallas', 'Houston', 'Toronto']
};
const GROUP_MATCH_SLOTS = {
  A: [
    { day: 11, hour: 21, minute: 0, city: 'Mexico City' },
    { day: 12, hour: 4, minute: 0, city: 'Guadalajara' },
    { day: 18, hour: 18, minute: 0, city: 'Atlanta' },
    { day: 19, hour: 3, minute: 0, city: 'Guadalajara' },
    { day: 25, hour: 3, minute: 0, city: 'Mexico City' },
    { day: 25, hour: 3, minute: 0, city: 'Monterrey' }
  ],
  B: [
    { day: 12, hour: 21, minute: 0, city: 'Toronto' },
    { day: 13, hour: 21, minute: 0, city: 'Los Angeles' },
    { day: 18, hour: 21, minute: 0, city: 'Los Angeles' },
    { day: 19, hour: 0, minute: 0, city: 'Vancouver' },
    { day: 24, hour: 21, minute: 0, city: 'Vancouver' },
    { day: 24, hour: 21, minute: 0, city: 'Seattle' }
  ],
  C: [
    { day: 14, hour: 0, minute: 0, city: 'New York/NJ' },
    { day: 14, hour: 3, minute: 0, city: 'Boston/Foxborough' },
    { day: 20, hour: 0, minute: 0, city: 'Boston/Foxborough' },
    { day: 20, hour: 3, minute: 0, city: 'Philadelphia' },
    { day: 25, hour: 0, minute: 0, city: 'Miami' },
    { day: 25, hour: 0, minute: 0, city: 'Atlanta' }
  ],
  D: [
    { day: 13, hour: 3, minute: 0, city: 'Los Angeles' },
    { day: 14, hour: 6, minute: 0, city: 'Vancouver' },
    { day: 19, hour: 21, minute: 0, city: 'Seattle' },
    { day: 20, hour: 6, minute: 0, city: 'San Francisco' },
    { day: 26, hour: 4, minute: 0, city: 'Los Angeles' },
    { day: 26, hour: 4, minute: 0, city: 'San Francisco' }
  ],
  E: [
    { day: 14, hour: 19, minute: 0, city: 'Houston' },
    { day: 15, hour: 1, minute: 0, city: 'Philadelphia' },
    { day: 20, hour: 22, minute: 0, city: 'Toronto' },
    { day: 21, hour: 2, minute: 0, city: 'Kansas City' },
    { day: 25, hour: 22, minute: 0, city: 'Philadelphia' },
    { day: 25, hour: 22, minute: 0, city: 'New York/NJ' }
  ],
  F: [
    { day: 14, hour: 22, minute: 0, city: 'Dallas' },
    { day: 15, hour: 4, minute: 0, city: 'Monterrey' },
    { day: 20, hour: 19, minute: 0, city: 'Houston' },
    { day: 21, hour: 6, minute: 0, city: 'Monterrey' },
    { day: 26, hour: 1, minute: 0, city: 'Dallas' },
    { day: 26, hour: 1, minute: 0, city: 'Kansas City' }
  ],
  G: [
    { day: 15, hour: 21, minute: 0, city: 'Seattle' },
    { day: 16, hour: 3, minute: 0, city: 'Los Angeles' },
    { day: 21, hour: 21, minute: 0, city: 'Los Angeles' },
    { day: 22, hour: 3, minute: 0, city: 'Vancouver' },
    { day: 27, hour: 5, minute: 0, city: 'Seattle' },
    { day: 27, hour: 5, minute: 0, city: 'Vancouver' }
  ],
  H: [
    { day: 15, hour: 18, minute: 0, city: 'Atlanta' },
    { day: 16, hour: 0, minute: 0, city: 'Miami' },
    { day: 21, hour: 18, minute: 0, city: 'Atlanta' },
    { day: 22, hour: 0, minute: 0, city: 'Miami' },
    { day: 27, hour: 2, minute: 0, city: 'Houston' },
    { day: 27, hour: 2, minute: 0, city: 'Guadalajara' }
  ],
  I: [
    { day: 16, hour: 21, minute: 0, city: 'New York/NJ' },
    { day: 17, hour: 0, minute: 0, city: 'Boston/Foxborough' },
    { day: 22, hour: 23, minute: 0, city: 'Philadelphia' },
    { day: 23, hour: 2, minute: 0, city: 'New York/NJ' },
    { day: 26, hour: 21, minute: 0, city: 'Boston/Foxborough' },
    { day: 26, hour: 21, minute: 0, city: 'Toronto' }
  ],
  J: [
    { day: 17, hour: 3, minute: 0, city: 'Kansas City' },
    { day: 17, hour: 6, minute: 0, city: 'San Francisco' },
    { day: 22, hour: 19, minute: 0, city: 'Dallas' },
    { day: 23, hour: 5, minute: 0, city: 'San Francisco' },
    { day: 28, hour: 4, minute: 0, city: 'Kansas City' },
    { day: 28, hour: 4, minute: 0, city: 'Dallas' }
  ],
  K: [
    { day: 17, hour: 19, minute: 0, city: 'Houston' },
    { day: 18, hour: 4, minute: 0, city: 'Mexico City' },
    { day: 23, hour: 19, minute: 0, city: 'Houston' },
    { day: 24, hour: 4, minute: 0, city: 'Guadalajara' },
    { day: 28, hour: 1, minute: 30, city: 'Miami' },
    { day: 28, hour: 1, minute: 30, city: 'Atlanta' }
  ],
  L: [
    { day: 17, hour: 22, minute: 0, city: 'Dallas' },
    { day: 18, hour: 1, minute: 0, city: 'Toronto' },
    { day: 23, hour: 22, minute: 0, city: 'Boston/Foxborough' },
    { day: 24, hour: 1, minute: 0, city: 'Toronto' },
    { day: 27, hour: 23, minute: 0, city: 'New York/NJ' },
    { day: 27, hour: 23, minute: 0, city: 'Philadelphia' }
  ]
};
const TEAM_GROUP_BY_ID = {
  cze: 'A', kor: 'A', mex: 'A', zaf: 'A',
  can: 'B', qat: 'B', sui: 'B', bih: 'B',
  bra: 'C', mar: 'C', sco: 'C', hai: 'C',
  aus: 'D', tur: 'D', usa: 'D', par: 'D',
  civ: 'E', ecu: 'E', ger: 'E', cuw: 'E',
  jpn: 'F', ned: 'F', tun: 'F', swe: 'F',
  bel: 'G', egy: 'G', irn: 'G', nzl: 'G',
  cpv: 'H', sau: 'H', esp: 'H', uru: 'H',
  fra: 'I', irq: 'I', nor: 'I', sen: 'I',
  arg: 'J', aut: 'J', jor: 'J', alg: 'J',
  col: 'K', drc: 'K', por: 'K', uzb: 'K',
  eng: 'L', pan: 'L', cro: 'L', gha: 'L'
};
const COUNTRY_CODE_BY_TEAM_ID = {
  ger: 'de', fra: 'fr', esp: 'es', eng: 'gb', por: 'pt', ned: 'nl', bel: 'be', aut: 'at',
  sui: 'ch', nor: 'no', swe: 'se', sco: 'gb-sct', cze: 'cz', cro: 'hr', bih: 'ba', tur: 'tr',
  bra: 'br', arg: 'ar', col: 'co', par: 'py', uru: 'uy', ecu: 'ec', usa: 'us', mex: 'mx',
  can: 'ca', pan: 'pa', hai: 'ht', cuw: 'cw', alg: 'dz', mar: 'ma', sen: 'sn', egy: 'eg',
  gha: 'gh', zaf: 'za', cpv: 'cv', civ: 'ci', drc: 'cd', tun: 'tn', jpn: 'jp', kor: 'kr',
  aus: 'au', irn: 'ir', sau: 'sa', qat: 'qa', uzb: 'uz', jor: 'jo', irq: 'iq', nzl: 'nz'
};
const TEAMS = [
  { id: 'ger', name: 'Germany', flag: '🇩🇪', region: 'UEFA' },
  { id: 'fra', name: 'France', flag: '🇫🇷', region: 'UEFA' },
  { id: 'esp', name: 'Spain', flag: '🇪🇸', region: 'UEFA' },
  { id: 'eng', name: 'England', flag: '🇬🇧', region: 'UEFA' },
  { id: 'por', name: 'Portugal', flag: '🇵🇹', region: 'UEFA' },
  { id: 'ned', name: 'Netherlands', flag: '🇳🇱', region: 'UEFA' },
  { id: 'bel', name: 'Belgium', flag: '🇧🇪', region: 'UEFA' },
  { id: 'aut', name: 'Austria', flag: '🇦🇹', region: 'UEFA' },
  { id: 'sui', name: 'Switzerland', flag: '🇨🇭', region: 'UEFA' },
  { id: 'nor', name: 'Norway', flag: '🇳🇴', region: 'UEFA' },
  { id: 'swe', name: 'Sweden', flag: '🇸🇪', region: 'UEFA' },
  { id: 'sco', name: 'Scotland', flag: '🇬🇧', region: 'UEFA' },
  { id: 'cze', name: 'Czech Republic', flag: '🇨🇿', region: 'UEFA' },
  { id: 'cro', name: 'Croatia', flag: '🇭🇷', region: 'UEFA' },
  { id: 'bih', name: 'Bosnia and Herzegovina', flag: '🇧🇦', region: 'UEFA' },
  { id: 'tur', name: 'Turkey', flag: '🇹🇷', region: 'UEFA' },
  { id: 'bra', name: 'Brazil', flag: '🇧🇷', region: 'CONMEBOL' },
  { id: 'arg', name: 'Argentina', flag: '🇦🇷', region: 'CONMEBOL' },
  { id: 'col', name: 'Colombia', flag: '🇨🇴', region: 'CONMEBOL' },
  { id: 'par', name: 'Paraguay', flag: '🇵🇾', region: 'CONMEBOL' },
  { id: 'uru', name: 'Uruguay', flag: '🇺🇾', region: 'CONMEBOL' },
  { id: 'ecu', name: 'Ecuador', flag: '🇪🇨', region: 'CONMEBOL' },
  { id: 'usa', name: 'USA', flag: '🇺🇸', region: 'CONCACAF' },
  { id: 'mex', name: 'Mexico', flag: '🇲🇽', region: 'CONCACAF' },
  { id: 'can', name: 'Canada', flag: '🇨🇦', region: 'CONCACAF' },
  { id: 'pan', name: 'Panama', flag: '🇵🇦', region: 'CONCACAF' },
  { id: 'hai', name: 'Haiti', flag: '🇭🇹', region: 'CONCACAF' },
  { id: 'cuw', name: 'Curacao', flag: '🇨🇼', region: 'CONCACAF' },
  { id: 'alg', name: 'Algeria', flag: '🇩🇿', region: 'CAF' },
  { id: 'mar', name: 'Morocco', flag: '🇲🇦', region: 'CAF' },
  { id: 'sen', name: 'Senegal', flag: '🇸🇳', region: 'CAF' },
  { id: 'egy', name: 'Egypt', flag: '🇪🇬', region: 'CAF' },
  { id: 'gha', name: 'Ghana', flag: '🇬🇭', region: 'CAF' },
  { id: 'zaf', name: 'South Africa', flag: '🇿🇦', region: 'CAF' },
  { id: 'cpv', name: 'Cape Verde', flag: '🇨🇻', region: 'CAF' },
  { id: 'civ', name: "Cote d'Ivoire", flag: '🇨🇮', region: 'CAF' },
  { id: 'drc', name: 'DR Congo', flag: '🇨🇩', region: 'CAF' },
  { id: 'tun', name: 'Tunisia', flag: '🇹🇳', region: 'CAF' },
  { id: 'jpn', name: 'Japan', flag: '🇯🇵', region: 'AFC' },
  { id: 'kor', name: 'South Korea', flag: '🇰🇷', region: 'AFC' },
  { id: 'aus', name: 'Australia', flag: '🇦🇺', region: 'AFC' },
  { id: 'irn', name: 'Iran', flag: '🇮🇷', region: 'AFC' },
  { id: 'sau', name: 'Saudi Arabia', flag: '🇸🇦', region: 'AFC' },
  { id: 'qat', name: 'Qatar', flag: '🇶🇦', region: 'AFC' },
  { id: 'uzb', name: 'Uzbekistan', flag: '🇺🇿', region: 'AFC' },
  { id: 'jor', name: 'Jordan', flag: '🇯🇴', region: 'AFC' },
  { id: 'irq', name: 'Iraq', flag: '🇮🇶', region: 'AFC' },
  { id: 'nzl', name: 'New Zealand', flag: '🇳🇿', region: 'OFC' }
];

function formatGmtOffset(date) {
  const totalMinutes = -date.getTimezoneOffset();
  const sign = totalMinutes >= 0 ? '+' : '-';
  const absMinutes = Math.abs(totalMinutes);
  const hours = Math.floor(absMinutes / 60);
  const minutes = absMinutes % 60;
  return minutes === 0 ? `GMT${sign}${hours}` : `GMT${sign}${hours}:${String(minutes).padStart(2, '0')}`;
}

function formatClientKickoff(isoDate) {
  const date = new Date(isoDate);
  const localTime = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short'
  }).format(date);

  return localTime;
}

function formatVenueKickoff(isoDate, timeZone) {
  return new Intl.DateTimeFormat(undefined, {
    timeZone,
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short'
  }).format(new Date(isoDate));
}

function normalizeCityName(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

function resolveVenueByCity(city) {
  const aliases = {
    newyorknj: 'newyork',
    bostonfoxborough: 'boston',
    kansas: 'kansascity'
  };

  const normalized = normalizeCityName(city);
  const target = aliases[normalized] || normalized;

  return HOST_VENUES.find((venue) => normalizeCityName(venue.city) === target) || null;
}

function buildGroupFixtures(groupLetter) {
  const groupTeamIds = Object.keys(TEAM_GROUP_BY_ID).filter((id) => TEAM_GROUP_BY_ID[id] === groupLetter);
  const groupTeams = groupTeamIds
    .map((id) => TEAMS.find((team) => team.id === id))
    .filter(Boolean)
    .slice(0, 4);

  if (groupTeams.length < 4) {
    return [];
  }

  const fixturesTemplate = [
    [0, 1],
    [2, 3],
    [0, 2],
    [1, 3],
    [0, 3],
    [1, 2]
  ];

  const groupIndex = GROUP_LETTERS.indexOf(groupLetter);
  const preferredCities = GROUP_KEY_VENUE_CITIES[groupLetter] || [];
  const slots = GROUP_MATCH_SLOTS[groupLetter] || [];
  const groupVenues = HOST_VENUES.filter((venue) => {
    const city = normalizeCityName(venue.city);
    return preferredCities.some((preferredCity) => normalizeCityName(preferredCity) === city);
  });
  const fallbackVenues = groupVenues.length > 0 ? groupVenues : HOST_VENUES;
  const baseDate = new Date(Date.UTC(2026, 5, 11 + Math.max(groupIndex, 0), 18, 0, 0));

  return fixturesTemplate.map((pair, index) => {
    const homeTeam = groupTeams[pair[0]];
    const awayTeam = groupTeams[pair[1]];
    const slot = slots[index] || null;
    const venue = (slot ? resolveVenueByCity(slot.city) : null) || fallbackVenues[index % fallbackVenues.length];
    const kickoffDate = slot
      ? new Date(Date.UTC(2026, 5, slot.day, slot.hour - 2, slot.minute || 0, 0))
      : new Date(baseDate.getTime() + index * 48 * 60 * 60 * 1000 + (index % 2) * 2 * 60 * 60 * 1000);

    return {
      id: `${groupLetter}-${homeTeam.id}-${awayTeam.id}`,
      stage: `Group ${groupLetter}`,
      kickoffUtc: kickoffDate.toISOString(),
      homeTeam,
      awayTeam,
      venue
    };
  });
}

function getVenueImageSrc(venue) {
  const raw = String(venue?.image || '').trim();
  if (!raw) {
    return '';
  }

  if (/^https?:\/\//i.test(raw) || raw.startsWith('/')) {
    return raw;
  }

  return '';
}

function getVenueFallbackSvg(venue) {
  const venueName = String(venue?.name || 'Stadium');
  const venueCity = String(venue?.city || 'Unknown city');
  const svg = `
    <svg xmlns='http://www.w3.org/2000/svg' width='320' height='180' viewBox='0 0 320 180'>
      <defs>
        <linearGradient id='g' x1='0' y1='0' x2='1' y2='1'>
          <stop offset='0%' stop-color='#183124'/>
          <stop offset='100%' stop-color='#2f5b36'/>
        </linearGradient>
      </defs>
      <rect width='320' height='180' rx='14' fill='url(#g)'/>
      <rect x='24' y='106' width='272' height='26' rx='4' fill='#0c1710'/>
      <rect x='44' y='54' width='232' height='44' rx='8' fill='#e7f4e8' fill-opacity='0.2'/>
      <text x='160' y='146' text-anchor='middle' fill='#dff5e1' font-family='Arial, sans-serif' font-size='14'>${venueName}</text>
      <text x='160' y='164' text-anchor='middle' fill='#a9d9af' font-family='Arial, sans-serif' font-size='12'>${venueCity}</text>
    </svg>
  `;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function getFlagImageSrc(team) {
  const id = String(team?.id || '').toLowerCase();
  const byId = COUNTRY_CODE_BY_TEAM_ID[id];
  if (byId) {
    return `https://flagcdn.com/w80/${byId}.png`;
  }

  const raw = String(team?.flag || '');
  if (/^https?:\/\//i.test(raw)) {
    return raw;
  }

  return '';
}

function normalizeTournamentStageName(stageName) {
  const raw = String(stageName || '').trim();
  const lower = raw.toLowerCase();

  if (!raw) {
    return '';
  }
  if (/group/.test(lower)) {
    return 'Group Stage';
  }
  if (lower === 'playoffs' || lower === 'play-offs' || lower === 'play offs') {
    return 'Play-offs';
  }
  if (lower === 'round of 32' || lower === 'round 32') {
    return 'Round of 32';
  }
  if (lower === 'round of 16' || lower === 'round 16') {
    return 'Round of 16';
  }
  if (lower === 'quarter-finals' || lower === 'quarter finals' || lower === 'quarterfinals') {
    return 'Quarter-finals';
  }
  if (lower === 'semi-finals' || lower === 'semi finals' || lower === 'semifinals') {
    return 'Semi-finals';
  }
  if (lower === '3rd place final' || lower === 'third place final') {
    return '3rd Place Final';
  }
  if (lower === 'final') {
    return 'Final';
  }

  return raw;
}

function App() {
  const [language, setLanguage] = useState(() => {
    const stored = String(localStorage.getItem(LANGUAGE_STORAGE_KEY) || '').toLowerCase();
    return stored === 'en' ? 'en' : 'de';
  });
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ name: '', username: '', password: '', workspaceName: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [token, setToken] = useState(localStorage.getItem('authToken') || '');
  const [tenantSlug, setTenantSlug] = useState(localStorage.getItem(TENANT_SLUG_STORAGE_KEY) || '');
  const [user, setUser] = useState(null);
  const [query, setQuery] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState(localStorage.getItem('selectedTeamId') || '');
  const [showGroupStage, setShowGroupStage] = useState(Boolean(localStorage.getItem('selectedTeamId')));
  const [teamFixtures, setTeamFixtures] = useState([]);
  const [fixturesLoading, setFixturesLoading] = useState(false);
  const [fixturesError, setFixturesError] = useState('');
  const [friends, setFriends] = useState([]);
  const [tips, setTips] = useState([]);
  const [friendName, setFriendName] = useState('');
  const [workspaceLogin, setWorkspaceLogin] = useState(null);
  const [workspaceLoginCopied, setWorkspaceLoginCopied] = useState(false);
  const [currentWorkspaceRole, setCurrentWorkspaceRole] = useState(null);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [resetPasswordError, setResetPasswordError] = useState('');
  const [rssItems, setRssItems] = useState([]);
  const [rssError, setRssError] = useState('');
  const [tipDraftByFixture, setTipDraftByFixture] = useState({});
  const [squadData, setSquadData] = useState({ coach: null, players: [] });
  const [squadLoading, setSquadLoading] = useState(false);
  const [squadError, setSquadError] = useState('');
  const [showSquadPanel, setShowSquadPanel] = useState(false);
    const [showTeamFormationPanel, setShowTeamFormationPanel] = useState(false);
  const [showNextPhasePanel, setShowNextPhasePanel] = useState(false);
  const [showNextPhaseDetailsPanel, setShowNextPhaseDetailsPanel] = useState(false);
  const [showRoarPanel, setShowRoarPanel] = useState(() => localStorage.getItem(ROAR_PANEL_STORAGE_KEY) === 'true');
  const [roarVolume, setRoarVolume] = useState(() => {
    const stored = Number(localStorage.getItem(ROAR_VOLUME_STORAGE_KEY));
    return Number.isFinite(stored) && stored >= 0 && stored <= 1 ? stored : 0.75;
  });
  const [roarTeamIds, setRoarTeamIds] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(ROAR_TEAM_IDS_STORAGE_KEY) || '[]');
      const validStored = Array.isArray(stored) ? stored.filter((teamId) => typeof teamId === 'string' && teamId.trim()) : [];
      return validStored.length > 0 ? validStored : (selectedTeamId ? [selectedTeamId] : []);
    } catch {
      return selectedTeamId ? [selectedTeamId] : [];
    }
  });
  const [nextPhaseData, setNextPhaseData] = useState([]);
  const [nextPhaseLoading, setNextPhaseLoading] = useState(false);
  const [nextPhaseError, setNextPhaseError] = useState('');
  const [selectedDiagramStageName, setSelectedDiagramStageName] = useState('');
  const [isPhaseViewActive, setIsPhaseViewActive] = useState(false);
  const [isDiagramModalOpen, setIsDiagramModalOpen] = useState(false);
  const [formationData, setFormationData] = useState([]);
  const [matchesResultsData, setMatchesResultsData] = useState([]);
  const [tournamentStructureData, setTournamentStructureData] = useState([]);
  const [teamViewError, setTeamViewError] = useState('');
  const [nextMatches, setNextMatches] = useState([]);
  const [allTournamentFixtures, setAllTournamentFixtures] = useState([]);
  const [nextMatchesLoading, setNextMatchesLoading] = useState(false);
  const [nextMatchesError, setNextMatchesError] = useState('');
  const [showNextMatchesPanel, setShowNextMatchesPanel] = useState(true);
  const [selectedNextMatchId, setSelectedNextMatchId] = useState('');
  const [allGroupStageFixtures, setAllGroupStageFixtures] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [showChatPanel, setShowChatPanel] = useState(true);
  const chatBottomRef = useRef(null);
  const lastTeamScoreRef = useRef(null);
  const roarVolumeRef = useRef(roarVolume);
  const roarAudioRef = useRef(null);
  const roarFadeTimerRef = useRef(null);
  const roarAudioSrcIndexRef = useRef(0);
  const nextMatchTipBlockRef = useRef(null);
  const nextMatchFriendSelectRef = useRef(null);
  const nextMatchFocusPendingRef = useRef(false);

  function createRoarAudio(srcIndex = 0) {
    const safeIndex = Math.max(0, Math.min(srcIndex, TEAM_SOUND_PATHS.length - 1));
    const audio = new Audio(TEAM_SOUND_PATHS[safeIndex]);
    audio.preload = 'auto';
    audio.playsInline = true;
    audio.volume = Math.max(0, Math.min(1, Number(roarVolumeRef.current) || 0));
    return audio;
  }

  const text = useMemo(() => {
    if (language === 'en') {
      return {
        appSubtitle: 'Simple group tip game with shared login and manual members list.',
        tickerLabel: 'RSS Live Ticker',
        sessionTitle: 'ROOAR World Cup 2026',
        sessionSubtitle: 'Select your team and hear the ROOAR.',
        roarToggle: 'ROOAR',
        gmailCalendar: 'Gmail Calendar',
        switchTeam: 'Switch Team',
        logout: 'Logout',
        loginLabel: 'Login',
        yourTeam: 'Your Team',
        noTeamSelected: 'No team selected yet',
        sharedLoginLoading: 'Shared login is loading...',
        resetPassword: 'Reset password',
        resetPasswordTitle: 'Generate a new password for the shared group login',
        addFriendsTitle: 'Add friends',
        friendNamePlaceholder: 'Name',
        addFriend: 'Add friend',
        modeLogin: 'Login',
        modeRegister: 'Register',
        registerTitle: 'Register group',
        loginTitle: 'Login',
        labelName: 'Name',
        namePlaceholder: 'Your name',
        labelGroupName: 'Group name',
        groupPlaceholder: 'e.g. Team Friends',
        labelUsername: 'Username',
        usernamePlaceholder: 'e.g. win2026%team-friends',
        labelPassword: 'Password',
        passwordPlaceholder: 'Password',
        registerHint: 'After registration, a shared login is generated automatically: ',
        squadTitle: 'Team',
        squadLoading: 'Loading team from Sportmonks...',
        noSquadData: 'No team data found.',
        noFormationData: 'No formation data available.',
        pointsStatsTitle: 'Friends points stats',
        pointsRules: '3 points for exact tip, 2 points for correct team.',
        noPoints: 'No points yet.',
        pointsCorrectTeam: 'Correct team',
      };
    }

    return {
      appSubtitle: 'Einfaches Gruppen-Tippspiel mit gemeinsamem Login und manueller Mitgliederliste.',
      tickerLabel: 'RSS Live-Ticker',
      sessionTitle: 'ROOAR World Cup 2026',
      sessionSubtitle: 'Wahle dein Team und hore den Rooar.',
      roarToggle: 'ROOAR',
      gmailCalendar: 'Gmail Kalender',
      switchTeam: 'Team wechseln',
      logout: 'Abmelden',
      loginLabel: 'Login',
      yourTeam: 'Dein Team',
      noTeamSelected: 'Noch kein Team ausgewahlt',
      sharedLoginLoading: 'Login wird geladen...',
      resetPassword: 'Passwort zurücksetzen',
      resetPasswordTitle: 'Neues Passwort fuer den gemeinsamen Login generieren',
      addFriendsTitle: 'Freunde hinzufügen',
      friendNamePlaceholder: 'Name',
      addFriend: 'Freund hinzufügen',
      modeLogin: 'Anmelden',
      modeRegister: 'Registrieren',
      registerTitle: 'Gruppe registrieren',
      loginTitle: 'Anmelden',
      labelName: 'Name',
      namePlaceholder: 'Dein Name',
      labelGroupName: 'Gruppenname',
      groupPlaceholder: 'z. B. Team Freunde',
      labelUsername: 'Benutzername',
      usernamePlaceholder: 'z. B. win2026%team-freunde',
      labelPassword: 'Passwort',
      passwordPlaceholder: 'Passwort',
      registerHint: 'Nach der Registrierung wird automatisch ein gemeinsamer Login erstellt: ',
      squadTitle: 'Mannschaft',
      squadLoading: 'Lade Mannschaft von Sportmonks...',
      noSquadData: 'Keine Mannschaftsdaten gefunden.',
      noFormationData: 'Keine Formation-Daten verfuegbar.',
      pointsStatsTitle: 'Freunde Punkte-Statistik',
      pointsRules: '3 Punkte fuer exakten Tipp, 2 Punkte fuer richtige Mannschaft.',
      noPoints: 'Noch keine Punkte vorhanden.',
      pointsCorrectTeam: 'Richtige Mannschaft',
    };
  }, [language]);

  const title = useMemo(() => (mode === 'login' ? text.loginTitle : text.registerTitle), [mode, text]);
  const languageSelectorValue = useMemo(() => (language === 'en' ? '🇬🇧' : '🇩🇪'), [language]);

  useEffect(() => {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  }, [language]);

  useEffect(() => {
    localStorage.setItem(ROAR_PANEL_STORAGE_KEY, String(showRoarPanel));
  }, [showRoarPanel]);

  useEffect(() => {
    return () => {
      if (roarFadeTimerRef.current) {
        clearInterval(roarFadeTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    localStorage.setItem(ROAR_VOLUME_STORAGE_KEY, String(roarVolume));
    roarVolumeRef.current = roarVolume;
    if (roarAudioRef.current) {
      roarAudioRef.current.volume = Math.max(0, Math.min(1, Number(roarVolume) || 0));
    }
  }, [roarVolume]);

  useEffect(() => {
    const audio = createRoarAudio(roarAudioSrcIndexRef.current);
    roarAudioRef.current = audio;

    return () => {
      if (roarAudioRef.current) {
        roarAudioRef.current.pause();
        roarAudioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    localStorage.setItem(ROAR_TEAM_IDS_STORAGE_KEY, JSON.stringify(roarTeamIds));
  }, [roarTeamIds]);

  useEffect(() => {
    // Reset score baseline when switching teams to avoid false ROOAR triggers.
    lastTeamScoreRef.current = null;
  }, [selectedTeamId]);

  const groupedTeams = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = !q
      ? TEAMS
      : TEAMS.filter((team) => team.name.toLowerCase().includes(q) || team.region.toLowerCase().includes(q));

    const groups = GROUP_LETTERS.map((letter) => [letter, []]);
    const idxMap = new Map(GROUP_LETTERS.map((letter, index) => [letter, index]));

    filtered
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach((team) => {
        const letter = TEAM_GROUP_BY_ID[team.id] || 'L';
        const idx = idxMap.get(letter);
        if (idx !== undefined) {
          groups[idx][1].push(team);
        }
      });

    return q ? groups.filter(([, teams]) => teams.length > 0) : groups;
  }, [query]);

  function persistTenantSlug(nextSlug) {
    const normalizedSlug = String(nextSlug || '').trim().toLowerCase();

    if (normalizedSlug) {
      localStorage.setItem(TENANT_SLUG_STORAGE_KEY, normalizedSlug);
    } else {
      localStorage.removeItem(TENANT_SLUG_STORAGE_KEY);
    }

    setTenantSlug(normalizedSlug);
  }

  function resolveTenantSlugFromPayload(payload) {
    if (payload?.tenant?.slug) {
      return String(payload.tenant.slug).trim().toLowerCase();
    }

    if (Array.isArray(payload?.tenants)) {
      const match = payload.tenants.find((tenant) => tenant?.slug);
      if (match?.slug) {
        return String(match.slug).trim().toLowerCase();
      }
    }

    if (Array.isArray(payload?.memberships)) {
      const match = payload.memberships.find((membership) => membership?.tenant?.slug);
      if (match?.tenant?.slug) {
        return String(match.tenant.slug).trim().toLowerCase();
      }
    }

    return '';
  }

  function resolveWorkspaceLoginFromPayload(payload, preferredSlug) {
    const normalizedSlug = String(preferredSlug || resolveTenantSlugFromPayload(payload) || '').trim().toLowerCase();

    if (payload?.generatedCredentials?.username && payload?.generatedCredentials?.password) {
      return payload.generatedCredentials;
    }

    if (Array.isArray(payload?.memberships)) {
      const matchingMembership = payload.memberships.find((membership) => {
        const membershipSlug = String(membership?.tenant?.slug || '').trim().toLowerCase();
        return normalizedSlug ? membershipSlug === normalizedSlug : Boolean(membershipSlug);
      });

      const sharedUsername = matchingMembership?.tenant?.sharedLoginUsername;
      const sharedPassword = matchingMembership?.tenant?.sharedLoginPassword;
      if (sharedUsername && sharedPassword) {
        return { username: sharedUsername, password: sharedPassword };
      }
    }

    if (Array.isArray(payload?.tenants)) {
      const matchingTenant = payload.tenants.find((tenant) => {
        const tenantSlug = String(tenant?.slug || '').trim().toLowerCase();
        return normalizedSlug ? tenantSlug === normalizedSlug : Boolean(tenantSlug);
      });

      const sharedUsername = matchingTenant?.sharedLoginUsername;
      const sharedPassword = matchingTenant?.sharedLoginPassword;
      if (sharedUsername && sharedPassword) {
        return { username: sharedUsername, password: sharedPassword };
      }
    }

    return null;
  }

  function resolveWorkspaceRoleFromPayload(payload, preferredSlug, currentUserId) {
    const normalizedSlug = String(preferredSlug || resolveTenantSlugFromPayload(payload) || '').trim().toLowerCase();
    if (!Array.isArray(payload?.memberships)) {
      return null;
    }

    const membership = payload.memberships.find((entry) => {
      const membershipSlug = String(entry?.tenant?.slug || '').trim().toLowerCase();
      const slugMatches = normalizedSlug ? membershipSlug === normalizedSlug : Boolean(membershipSlug);
      const userMatches = currentUserId ? String(entry?.userId || '') === String(currentUserId) : true;
      return slugMatches && userMatches;
    });

    return membership?.role || null;
  }

  function buildAuthHeaders(extraHeaders = {}) {
    return {
      ...extraHeaders,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(tenantSlug ? { 'X-Tenant-Slug': tenantSlug } : {}),
    };
  }

  async function parseJsonResponse(response, fallbackMessage) {
    const contentType = String(response.headers.get('content-type') || '').toLowerCase();
    const rawBody = await response.text();
    const statusInfo = `HTTP ${response.status}`;

    if (!rawBody) {
      return {};
    }

    if (contentType.includes('application/json') || contentType.includes('+json')) {
      try {
        return JSON.parse(rawBody);
      } catch {
        throw new Error(fallbackMessage || 'Antwort vom Server war kein gueltiges JSON.');
      }
    }

    try {
      return JSON.parse(rawBody);
    } catch {
      throw new Error(
        fallbackMessage || `Der Server hat keine JSON-Antwort geliefert (${statusInfo}). Bitte API-URL/Redirect pruefen.`
      );
    }
  }

  function flattenFriendTips(friendRows) {
    return (Array.isArray(friendRows) ? friendRows : []).flatMap((friend) => {
      const tipsByFixture = friend?.tips && typeof friend.tips === 'object' ? friend.tips : {};

      return Object.entries(tipsByFixture)
        .filter(([, tip]) => tip && typeof tip === 'object')
        .map(([fixtureId, tip]) => ({
          id: `${friend.id}:${fixtureId}`,
          fixture_id: fixtureId,
          friend_id: friend.id,
          friend_name: friend.name,
          home_tip: String(tip.home ?? ''),
          away_tip: String(tip.away ?? ''),
        }));
    });
  }

  function buildCurrentUserTipsPayload(currentTips, currentUserId) {
    if (!currentUserId) {
      return {};
    }

    return currentTips.reduce((acc, tip) => {
      if (String(tip.friend_id) !== String(currentUserId)) {
        return acc;
      }

      acc[String(tip.fixture_id)] = {
        home: String(tip.home_tip ?? ''),
        away: String(tip.away_tip ?? ''),
      };
      return acc;
    }, {});
  }

  async function loadMe(currentToken) {
    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${currentToken}` }
    });

    if (!response.ok) {
      throw new Error('Session konnte nicht geladen werden.');
    }

    const data = await response.json();
    setUser(data.user);
    const nextTenantSlug = tenantSlug || resolveTenantSlugFromPayload(data);
    persistTenantSlug(nextTenantSlug);
    setWorkspaceLogin(resolveWorkspaceLoginFromPayload(data, nextTenantSlug));
    setCurrentWorkspaceRole(resolveWorkspaceRoleFromPayload(data, nextTenantSlug, data?.user?.id));
  }

  useEffect(() => {
    if (!token) {
      setUser(null);
      setCurrentWorkspaceRole(null);
      setShowGroupStage(false);
      return;
    }

    loadMe(token).catch(() => {
      localStorage.removeItem('authToken');
      setToken('');
      setUser(null);
      setCurrentWorkspaceRole(null);
    });
  }, [token]);

  async function submitForm(event) {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      const endpoint = mode === 'login' ? '/auth/login' : '/auth/signup';
      const payload = mode === 'login'
        ? { username: form.username, password: form.password }
        : {
          name: form.name,
          workspaceName: form.workspaceName,
        };

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Anfrage fehlgeschlagen.');
      }

      localStorage.setItem('authToken', data.token);
      setToken(data.token);
      setUser(data.user);
      const nextTenantSlug = resolveTenantSlugFromPayload(data);
      persistTenantSlug(nextTenantSlug);
      setWorkspaceLogin(resolveWorkspaceLoginFromPayload(data, nextTenantSlug));
      setForm({
        name: '',
        username: data?.generatedCredentials?.username || '',
        password: '',
        workspaceName: '',
      });
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setLoading(false);
    }
  }

  function logout() {
    localStorage.removeItem('authToken');
    localStorage.removeItem(TENANT_SLUG_STORAGE_KEY);
    setToken('');
    setTenantSlug('');
    setUser(null);
    setCurrentWorkspaceRole(null);
    setWorkspaceLogin(null);
  }

  function playRoar() {
    const audio = roarAudioRef.current || createRoarAudio(roarAudioSrcIndexRef.current);
    audio.volume = Math.max(0, Math.min(1, Number(roarVolumeRef.current) || 0));
    audio.currentTime = 0;
    roarAudioRef.current = audio;

    if (roarFadeTimerRef.current) {
      clearInterval(roarFadeTimerRef.current);
      roarFadeTimerRef.current = null;
    }

    audio.play().catch(() => {
      // Try alternate source paths once before giving up.
      const nextIndex = roarAudioSrcIndexRef.current + 1;
      if (nextIndex >= TEAM_SOUND_PATHS.length) {
        return;
      }
      roarAudioSrcIndexRef.current = nextIndex;
      const fallbackAudio = createRoarAudio(nextIndex);
      fallbackAudio.currentTime = 0;
      roarAudioRef.current = fallbackAudio;
      fallbackAudio.play().catch(() => {
        // Browser kann Audio blocken, wenn kein direkter User-Klick vorlag.
      });
    });

    const fadeDurationMs = 1000;
    const fadeStepMs = 120;

    const startFadeOut = () => {
      if (roarFadeTimerRef.current) {
        clearInterval(roarFadeTimerRef.current);
      }

      const startVolume = Math.max(0, Math.min(1, Number(roarVolumeRef.current) || 0));
      const fadeStart = Math.max(0, (audio.duration || 0) * 1000 - fadeDurationMs);

      window.setTimeout(() => {
        let steps = Math.max(1, Math.ceil(fadeDurationMs / fadeStepMs));
        roarFadeTimerRef.current = window.setInterval(() => {
          steps -= 1;
          const nextVolume = Math.max(0, (startVolume * steps) / Math.max(1, Math.ceil(fadeDurationMs / fadeStepMs)));
          audio.volume = nextVolume;

          if (steps <= 0) {
            clearInterval(roarFadeTimerRef.current);
            roarFadeTimerRef.current = null;
            audio.volume = 0;
          }
        }, fadeStepMs);
      }, Math.max(0, fadeStart));
    };

    if (Number.isFinite(audio.duration) && audio.duration > 0) {
      startFadeOut();
    } else {
      const onLoadedMetadata = () => {
        audio.removeEventListener('loadedmetadata', onLoadedMetadata);
        startFadeOut();
      };
      audio.addEventListener('loadedmetadata', onLoadedMetadata);
    }
  }

  function toggleRoarTeam(teamId) {
    setRoarTeamIds((prev) => {
      const next = new Set(prev.filter(Boolean));
      if (next.has(teamId)) {
        next.delete(teamId);
      } else {
        next.add(teamId);
      }

      const nextList = Array.from(next);
      return nextList.length > 0 ? nextList : [selectedTeamId || teamId];
    });
  }

  function buildGoogleCalendarUrl(fixture, team) {
    const startDate = new Date(fixture.kickoffUtc);
    const endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000);
    const toGoogleDate = (date) => date.toISOString().replace(/[-:]/g, '').replace('.000', '');
    const opponent = fixture.homeTeam.name === team.name ? fixture.awayTeam.name : fixture.homeTeam.name;
    const text = `${team.name} vs ${opponent} (Group Stage)`;
    const localKickoff = formatClientKickoff(fixture.kickoffUtc);
    const venueKickoff = formatVenueKickoff(fixture.kickoffUtc, fixture.venue.timeZone);
    const details = `Venue: ${fixture.venue.name}, ${fixture.venue.city}\nVenue kickoff: ${venueKickoff}\nYour local time: ${localKickoff}`;
    const location = `${fixture.venue.name}, ${fixture.venue.city}, ${fixture.venue.country}`;

    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(text)}&dates=${toGoogleDate(startDate)}/${toGoogleDate(endDate)}&details=${encodeURIComponent(details)}&location=${encodeURIComponent(location)}`;
  }

  function buildIcs(fixtures, team) {
    const lines = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Rooar//Group Stage//DE'
    ];

    fixtures.forEach((fixture) => {
      const startDate = new Date(fixture.kickoffUtc);
      const endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000);
      const toIcsDate = (date) => date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
      const opponent = fixture.homeTeam.name === team.name ? fixture.awayTeam.name : fixture.homeTeam.name;

      lines.push('BEGIN:VEVENT');
      lines.push(`UID:${fixture.id}@rooar.local`);
      lines.push(`DTSTAMP:${toIcsDate(new Date())}`);
      lines.push(`DTSTART:${toIcsDate(startDate)}`);
      lines.push(`DTEND:${toIcsDate(endDate)}`);
      lines.push(`SUMMARY:${team.name} vs ${opponent}`);
      lines.push(`LOCATION:${fixture.venue.name}, ${fixture.venue.city}, ${fixture.venue.country}`);
      lines.push(`DESCRIPTION:Group Stage - ${fixture.stage}`);
      lines.push('END:VEVENT');
    });

    lines.push('END:VCALENDAR');
    return lines.join('\r\n');
  }

  function downloadIcs(fixtures, team) {
    if (!fixtures.length || !team) {
      return;
    }
    const content = buildIcs(fixtures, team);
    const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `group-stage-${team.id}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  function normalizeNameLocal(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  }

  const TEAM_NAME_ALIASES = {
    usa: 'usa',
    unitedstates: 'usa',
    usmnt: 'usa',
    turkey: 'tur',
    turkiye: 'tur'
  };

  function resolveLocalTeam(teamId, teamName) {
    const rawId = String(teamId || '').toLowerCase();
    if (rawId) {
      const byId = TEAMS.find((team) => team.id === rawId);
      if (byId) {
        return byId;
      }
    }

    const normalizedName = normalizeNameLocal(teamName || '');
    if (!normalizedName) {
      return null;
    }

    const aliasId = TEAM_NAME_ALIASES[normalizedName];
    if (aliasId) {
      return TEAMS.find((team) => team.id === aliasId) || null;
    }

    return TEAMS.find((team) => normalizeNameLocal(team.name) === normalizedName) || null;
  }

  function getCanonicalTeamName(teamId, fallbackName) {
    return resolveLocalTeam(teamId, fallbackName)?.name || fallbackName || String(teamId || '');
  }

  function getCanonicalTeamMeta(teamId, fallbackName) {
    const localTeam = resolveLocalTeam(teamId, fallbackName);
    if (localTeam) {
      return localTeam;
    }

    return {
      id: String(teamId || '').toLowerCase(),
      name: fallbackName || String(teamId || ''),
      flag: '🏳️'
    };
  }

  function enrichFixtureTeams(fixtures) {
    return fixtures.map((fixture) => {
      const homeLocal = resolveLocalTeam(fixture.homeTeam?.id, fixture.homeTeam?.name);
      const awayLocal = resolveLocalTeam(fixture.awayTeam?.id, fixture.awayTeam?.name);

      return {
        ...fixture,
        homeTeam: {
          ...fixture.homeTeam,
          id: fixture.homeTeam?.id || homeLocal?.id || '',
          name: homeLocal?.name || fixture.homeTeam?.name || 'Home',
          flag: homeLocal?.flag || fixture.homeTeam?.flag || '🏳️'
        },
        awayTeam: {
          ...fixture.awayTeam,
          id: fixture.awayTeam?.id || awayLocal?.id || '',
          name: awayLocal?.name || fixture.awayTeam?.name || 'Away',
          flag: awayLocal?.flag || fixture.awayTeam?.flag || '🏳️'
        }
      };
    });
  }

  function selectedTeamsScoreFromFixtures(fixtures, teamIds) {
    const selectedIds = new Set((Array.isArray(teamIds) ? teamIds : []).map((teamId) => String(teamId)));
    if (selectedIds.size === 0) {
      return 0;
    }

    return fixtures.reduce((acc, fixture) => {
      const homeId = String(fixture.homeTeam?.id || '').toLowerCase();
      const awayId = String(fixture.awayTeam?.id || '').toLowerCase();
      if (selectedIds.has(homeId)) {
        acc += Number(fixture.homeScore || 0);
      }
      if (selectedIds.has(awayId)) {
        acc += Number(fixture.awayScore || 0);
      }
      return acc;
    }, 0);
  }

  function parseFixtureKickoffMs(fixture) {
    const kickoffUtc = String(fixture?.kickoffUtc || '').trim();
    if (!kickoffUtc) {
      return null;
    }

    const kickoffMs = Date.parse(kickoffUtc);
    return Number.isFinite(kickoffMs) ? kickoffMs : null;
  }

  function isGroupStageName(stageName) {
    return /group/i.test(String(stageName || ''));
  }

  function isFixturePlayed(fixture) {
    const status = String(fixture?.status || '').trim().toLowerCase();
    const hasResultStatus = status && !/(not started|upcoming|scheduled|to be announced|postponed|canceled|cancelled)/i.test(status);
    const homeGoals = Number(fixture?.homeScore || 0);
    const awayGoals = Number(fixture?.awayScore || 0);
    const hasNumericResult = Number.isFinite(homeGoals) && Number.isFinite(awayGoals) && (homeGoals > 0 || awayGoals > 0);
    return Boolean(hasResultStatus || hasNumericResult);
  }

  function buildProjectedNextPhaseFixtures(groupLetter, standingsRows) {
    if (!groupLetter || !Array.isArray(standingsRows) || standingsRows.length < 2) {
      return [];
    }

    const topTwo = standingsRows.slice(0, 2).map((row) => getCanonicalTeamMeta(row.teamId, row.teamName));
    if (topTwo.length < 2) {
      return [];
    }

    const kickoffDate = new Date(Date.UTC(2026, 6, 1, 18, 0, 0));
    const venue = HOST_VENUES[0] || {
      name: 'TBD Stadium',
      city: 'TBD',
      country: 'USA',
      timeZone: 'America/New_York',
      image: ''
    };

    return [
      {
        id: `projected-${groupLetter}-${topTwo[0].id}-${topTwo[1].id}`,
        stage: 'Round of 32',
        round: 'Projected',
        kickoffUtc: kickoffDate.toISOString(),
        homeScore: 0,
        awayScore: 0,
        homeTeam: {
          id: topTwo[0].id,
          name: topTwo[0].name,
          flag: topTwo[0].flag || '🏳️'
        },
        awayTeam: {
          id: topTwo[1].id,
          name: topTwo[1].name,
          flag: topTwo[1].flag || '🏳️'
        },
        venue: {
          ...venue,
          image: ''
        }
      }
    ];
  }

  function buildTournamentStructureTemplate(groupLetter, standingsRows) {
    const stagePlan = [
      { stage: 'Round of 32', matches: 16 },
      { stage: 'Round of 16', matches: 8 },
      { stage: 'Quarter-finals', matches: 4 },
      { stage: 'Semi-finals', matches: 2 },
      { stage: '3rd Place Final', matches: 1 },
      { stage: 'Final', matches: 1 }
    ];

    const topTwo = Array.isArray(standingsRows)
      ? standingsRows.slice(0, 2).map((row) => getCanonicalTeamMeta(row.teamId, row.teamName))
      : [];

    const baseDate = Date.UTC(2026, 6, 1, 18, 0, 0);
    const venue = HOST_VENUES[0] || {
      name: 'TBD Stadium',
      city: 'TBD',
      country: 'USA',
      timeZone: 'America/New_York',
      image: ''
    };

    return stagePlan.map((plan, stageIndex) => {
      const fixtures = Array.from({ length: plan.matches }, (_, matchIndex) => {
        const kickoffUtc = new Date(
          baseDate + ((stageIndex * 3 + matchIndex) * 24 * 60 * 60 * 1000)
        ).toISOString();

        const isFirstProjectedMatch = stageIndex === 0 && matchIndex === 0 && topTwo.length >= 2;
        const homeTeam = isFirstProjectedMatch
          ? topTwo[0]
          : { id: `tbd-${plan.stage}-${matchIndex}-h`, name: 'TBD', flag: '🏳️' };
        const awayTeam = isFirstProjectedMatch
          ? topTwo[1]
          : { id: `tbd-${plan.stage}-${matchIndex}-a`, name: 'TBD', flag: '🏳️' };

        return {
          id: `template-${groupLetter || 'x'}-${plan.stage}-${matchIndex + 1}`,
          stage: plan.stage,
          round: 'Projected',
          kickoffUtc,
          homeScore: 0,
          awayScore: 0,
          homeTeam: {
            id: homeTeam.id,
            name: homeTeam.name,
            flag: homeTeam.flag || '🏳️'
          },
          awayTeam: {
            id: awayTeam.id,
            name: awayTeam.name,
            flag: awayTeam.flag || '🏳️'
          },
          venue: {
            ...venue,
            image: ''
          }
        };
      });

      return {
        stage: plan.stage,
        fixtures
      };
    });
  }

  function buildLiveTickerItems(fixtures, teamName) {
    const nowMs = Date.now();

    const sortedFixtures = [...(Array.isArray(fixtures) ? fixtures : [])].sort((a, b) => {
      const kickoffA = parseFixtureKickoffMs(a);
      const kickoffB = parseFixtureKickoffMs(b);
      if (kickoffA === null && kickoffB === null) {
        return 0;
      }
      if (kickoffA === null) {
        return 1;
      }
      if (kickoffB === null) {
        return -1;
      }
      return kickoffA - kickoffB;
    });

    const tickerRows = sortedFixtures.slice(0, 8).map((fixture) => {
      const homeName = String(fixture.homeTeam?.name || 'Home');
      const awayName = String(fixture.awayTeam?.name || 'Away');
      const homeScore = Number(fixture.homeScore || 0);
      const awayScore = Number(fixture.awayScore || 0);

      const kickoffMs = parseFixtureKickoffMs(fixture);
      const elapsedMinutes = kickoffMs === null ? null : Math.floor((nowMs - kickoffMs) / 60000);
      const isStarted = elapsedMinutes !== null && elapsedMinutes >= 0;
      const isLikelyLive = isStarted && elapsedMinutes <= 150;
      const hasScore = homeScore > 0 || awayScore > 0;

      if (!isStarted) {
        const kickoffLabel = fixture.kickoffUtc ? formatClientKickoff(fixture.kickoffUtc) : 'Zeit offen';
        return { title: `${homeName} vs ${awayName} - Start ${kickoffLabel}` };
      }

      if (isLikelyLive) {
        const minuteLabel = elapsedMinutes !== null ? `${Math.max(1, Math.min(elapsedMinutes, 120))}'` : "'";
        if (hasScore || (elapsedMinutes !== null && elapsedMinutes >= 1)) {
          return { title: `LIVE ${minuteLabel}: ${homeName} ${homeScore}:${awayScore} ${awayName}` };
        }
      }

      return { title: `FT: ${homeName} ${homeScore}:${awayScore} ${awayName}` };
    });

    if (tickerRows.length > 0) {
      return tickerRows;
    }

    return [{ title: `${teamName}: Keine aktuellen Spiele verfuegbar.` }];
  }

  function normalizeFixtureIdKey(value) {
    const raw = String(value || '').trim();
    if (!raw) {
      return '';
    }

    const digits = raw.replace(/\D/g, '');
    if (digits) {
      return digits;
    }

    return raw.toLowerCase();
  }

  function resolveFixtureIdValue(fixtureOrId) {
    if (fixtureOrId && typeof fixtureOrId === 'object') {
      const maybeFixture = fixtureOrId;
      const candidate = maybeFixture.id ?? maybeFixture.fixture_id ?? maybeFixture.fixtureId;
      return String(candidate || '').trim();
    }

    return String(fixtureOrId || '').trim();
  }

  function loadTipsForFixture(fixtureOrId) {
    const fixtureRaw = resolveFixtureIdValue(fixtureOrId);
    if (!fixtureRaw) {
      return [];
    }

    const fixtureNormalized = normalizeFixtureIdKey(fixtureRaw);

    return tips.filter((tip) => {
      const tipRaw = String(tip.fixture_id || '').trim();
      if (!tipRaw) {
        return false;
      }

      if (tipRaw === fixtureRaw) {
        return true;
      }

      const tipNormalized = normalizeFixtureIdKey(tipRaw);
      if (tipNormalized && fixtureNormalized && tipNormalized === fixtureNormalized) {
        return true;
      }

      return false;
    });
  }

  function getOutcome(home, away) {
    if (home > away) {
      return 'home';
    }
    if (away > home) {
      return 'away';
    }
    return 'draw';
  }

  function calculateTipPoints(tip, fixture) {
    const tipHome = Number(tip.home_tip);
    const tipAway = Number(tip.away_tip);
    const realHome = Number(fixture.homeScore || 0);
    const realAway = Number(fixture.awayScore || 0);

    if (tipHome === realHome && tipAway === realAway) {
      return 3;
    }

    if (getOutcome(tipHome, tipAway) === getOutcome(realHome, realAway)) {
      return 2;
    }

    return 0;
  }

  function getTipDraft(fixtureId) {
    return tipDraftByFixture[fixtureId] || { friendId: '', homeTip: '', awayTip: '' };
  }

  function setTipDraft(fixtureId, patch) {
    setTipDraftByFixture((prev) => ({
      ...prev,
      [fixtureId]: {
        ...getTipDraft(fixtureId),
        ...patch
      }
    }));
  }

  function chooseTeam(teamId) {
    setSelectedTeamId(teamId);
    localStorage.setItem('selectedTeamId', teamId);
    playRoar();
    setShowGroupStage(true);
    setTeamFixtures([]);
    setFixturesError('');
    setRoarTeamIds((prev) => (prev.includes(teamId) ? prev : [...prev, teamId]));
  }

  function renderRows(teams) {
    const rows = [];
    for (let i = 0; i < teams.length; i += 4) {
      rows.push(teams.slice(i, i + 4));
    }
    return rows;
  }

  const selectedTeam = TEAMS.find((team) => team.id === selectedTeamId) || null;
  const selectedGroupLetter = selectedTeam ? (TEAM_GROUP_BY_ID[selectedTeam.id] || null) : null;
  const localFallbackGroupFixtures = useMemo(() => {
    if (!selectedGroupLetter) {
      return [];
    }

    return buildGroupFixtures(selectedGroupLetter);
  }, [selectedGroupLetter]);

  const selectedGroupFixtures = useMemo(() => {
    if (!selectedGroupLetter) {
      return [];
    }

    const canonicalGroupTeamIds = new Set(
      Object.keys(TEAM_GROUP_BY_ID).filter((teamId) => TEAM_GROUP_BY_ID[teamId] === selectedGroupLetter)
    );

    const normalizeFixtureTeamId = (teamId, teamName) => {
      const localTeam = resolveLocalTeam(teamId, teamName);
      if (localTeam?.id) {
        return localTeam.id;
      }
      return String(teamId || '').toLowerCase();
    };

    // Prefer allGroupStageFixtures (all group matches) over teamFixtures (single team only)
    const liveSource = allGroupStageFixtures.length > 0 ? allGroupStageFixtures : teamFixtures;

    const liveCandidates = [...liveSource]
      .map((fixture) => {
        const homeId = normalizeFixtureTeamId(fixture.homeTeam?.id, fixture.homeTeam?.name);
        const awayId = normalizeFixtureTeamId(fixture.awayTeam?.id, fixture.awayTeam?.name);
        return {
          fixture,
          homeId,
          awayId
        };
      })
      .filter((row) => canonicalGroupTeamIds.has(row.homeId) && canonicalGroupTeamIds.has(row.awayId));

    const fixtureByPairKey = new Map();
    liveCandidates.forEach((row) => {
      // no-op — needed for the filter above
    });

    // If we have live fixtures for this group, return them directly with
    // normalised app-IDs. This avoids pair-key mismatch between numeric
    // SportMonks IDs (e.g. 18576) and local app IDs (e.g. "mex").
    if (liveCandidates.length > 0) {
      return liveCandidates.map((row) => ({
        ...row.fixture,
        homeTeam: {
          ...row.fixture.homeTeam,
          id: row.homeId,
        },
        awayTeam: {
          ...row.fixture.awayTeam,
          id: row.awayId,
        },
      }));
    }

    return localFallbackGroupFixtures;
  }, [allGroupStageFixtures, localFallbackGroupFixtures, selectedGroupLetter, teamFixtures]);

  const selectedGroupTeams = useMemo(() => {
    if (!selectedGroupLetter) {
      return [];
    }
    return TEAMS.filter((team) => TEAM_GROUP_BY_ID[team.id] === selectedGroupLetter).sort((a, b) => a.name.localeCompare(b.name));
  }, [selectedGroupLetter]);

  const tickerText = useMemo(() => {
    if (!rssItems.length) {
      return rssError || 'Live-Ticker derzeit nicht verfuegbar.';
    }
    return rssItems.map((item) => item.title).join('   •   ');
  }, [rssError, rssItems]);

  const fixtureCatalogById = useMemo(() => {
    const fixtureById = new Map();
    const sourceCollections = [
      selectedGroupFixtures,
      nextMatches,
      teamFixtures,
      allGroupStageFixtures,
      matchesResultsData,
      nextPhaseData,
      ...(Array.isArray(tournamentStructureData)
        ? tournamentStructureData.map((stage) => (Array.isArray(stage?.fixtures) ? stage.fixtures : []))
        : [])
    ];

    sourceCollections.forEach((collection) => {
      (Array.isArray(collection) ? collection : []).forEach((fixture) => {
        const fixtureId = String(fixture?.id || '').trim();
        if (!fixtureId || fixtureById.has(fixtureId)) {
          return;
        }
        fixtureById.set(fixtureId, fixture);
      });
    });

    return fixtureById;
  }, [
    allGroupStageFixtures,
    matchesResultsData,
    nextMatches,
    nextPhaseData,
    selectedGroupFixtures,
    teamFixtures,
    tournamentStructureData
  ]);

  const friendPointsTable = useMemo(() => {
    const pointsByFriend = new Map();

    tips.forEach((tip) => {
      const key = String(tip.friend_id || tip.friend_name);
      const prev = pointsByFriend.get(key) || {
        friendId: tip.friend_id,
        friendName: tip.friend_name,
        points: 0,
        exactHits: 0,
        correctTeamHits: 0,
        tipsCount: 0,
        tipDetails: []
      };

      const fixtureId = String(tip.fixture_id || '').trim();
      const fixture = fixtureCatalogById.get(fixtureId) || null;

      prev.tipsCount += 1;
      prev.tipDetails.push({
        fixtureId,
        matchLabel: fixture
          ? `${fixture.homeTeam?.name || 'Home'} vs ${fixture.awayTeam?.name || 'Away'}`
          : `Match ${fixtureId || '-'}`,
        tipScore: `${tip.home_tip} : ${tip.away_tip}`,
        kickoffMs: fixture ? parseFixtureKickoffMs(fixture) : null
      });

      if (fixture && isFixturePlayed(fixture)) {
        const points = calculateTipPoints(tip, fixture);
        prev.points += points;
        if (points === 3) {
          prev.exactHits += 1;
        } else if (points === 2) {
          prev.correctTeamHits += 1;
        }
      }

      pointsByFriend.set(key, prev);
    });

    return Array.from(pointsByFriend.values())
      .map((row) => ({
        ...row,
        tipDetails: row.tipDetails
          .slice()
          .sort((a, b) => {
            if (a.kickoffMs === null && b.kickoffMs === null) return 0;
            if (a.kickoffMs === null) return 1;
            if (b.kickoffMs === null) return -1;
            return a.kickoffMs - b.kickoffMs;
          })
      }))
      .sort((a, b) => {
        if (b.points !== a.points) {
          return b.points - a.points;
        }
        if (b.exactHits !== a.exactHits) {
          return b.exactHits - a.exactHits;
        }
        return String(a.friendName).localeCompare(String(b.friendName));
      });
  }, [fixtureCatalogById, tips]);

  const groupStandings = useMemo(() => {
    const table = new Map();

    const resolveCanonicalTeamKey = (teamId, teamName) => {
      const rawId = String(teamId || '').toLowerCase();
      if (rawId && TEAM_GROUP_BY_ID[rawId]) {
        return rawId;
      }

      const localMatch = resolveLocalTeam(teamId, teamName);
      if (localMatch?.id) {
        return localMatch.id;
      }

      const normalizedName = normalizeNameLocal(teamName || '');

      return rawId || normalizedName;
    };

    const ensureTeam = (teamId, teamName) => {
      const key = resolveCanonicalTeamKey(teamId, teamName);
      if (!key) {
        return null;
      }

      const canonicalName = TEAMS.find((team) => team.id === key)?.name
        || teamName
        || key;

      if (!table.has(key)) {
        table.set(key, {
          teamId: key,
          teamName: canonicalName,
          played: 0,
          won: 0,
          draw: 0,
          lost: 0,
          goalsFor: 0,
          goalsAgainst: 0,
          goalDiff: 0,
          points: 0
        });
      } else if (canonicalName && !table.get(key).teamName) {
        table.get(key).teamName = canonicalName;
      }

      return table.get(key);
    };

    selectedGroupTeams.forEach((team) => {
      ensureTeam(team.id, team.name);
    });

    selectedGroupFixtures.forEach((fixture) => {
      const homeId = String(fixture.homeTeam?.id || '').toLowerCase();
      const awayId = String(fixture.awayTeam?.id || '').toLowerCase();
      const homeRow = ensureTeam(homeId, fixture.homeTeam?.name || homeId);
      const awayRow = ensureTeam(awayId, fixture.awayTeam?.name || awayId);

      if (!homeRow || !awayRow) {
        return;
      }

      const status = String(fixture.status || '').trim().toLowerCase();
      const hasResultStatus = status && !/(not started|upcoming|scheduled|to be announced|postponed|canceled|cancelled)/i.test(status);
      const homeGoals = Number(fixture.homeScore || 0);
      const awayGoals = Number(fixture.awayScore || 0);
      const hasNumericResult = Number.isFinite(homeGoals) && Number.isFinite(awayGoals) && (homeGoals > 0 || awayGoals > 0);
      const isPlayed = hasResultStatus || hasNumericResult;

      if (!isPlayed) {
        return;
      }

      homeRow.played += 1;
      awayRow.played += 1;
      homeRow.goalsFor += homeGoals;
      homeRow.goalsAgainst += awayGoals;
      awayRow.goalsFor += awayGoals;
      awayRow.goalsAgainst += homeGoals;

      if (homeGoals > awayGoals) {
        homeRow.won += 1;
        awayRow.lost += 1;
        homeRow.points += 3;
      } else if (homeGoals < awayGoals) {
        awayRow.won += 1;
        homeRow.lost += 1;
        awayRow.points += 3;
      } else {
        homeRow.draw += 1;
        awayRow.draw += 1;
        homeRow.points += 1;
        awayRow.points += 1;
      }
    });

    const rows = Array.from(table.values()).map((row) => ({
      ...row,
      goalDiff: row.goalsFor - row.goalsAgainst
    }));

    rows.sort((a, b) => {
      if (b.points !== a.points) {
        return b.points - a.points;
      }
      if (b.goalDiff !== a.goalDiff) {
        return b.goalDiff - a.goalDiff;
      }
      if (b.goalsFor !== a.goalsFor) {
        return b.goalsFor - a.goalsFor;
      }
      return String(a.teamName || '').localeCompare(String(b.teamName || ''));
    });

    return rows.map((row, index) => ({ ...row, rank: index + 1 }));
  }, [selectedGroupFixtures, selectedGroupTeams]);

  useEffect(() => {
    if (!showGroupStage || !selectedTeam) {
      setNextPhaseData([]);
      setMatchesResultsData([]);
      setTournamentStructureData([]);
      setNextPhaseLoading(false);
      setNextPhaseError('');
      setTeamViewError('');
      return;
    }

    const sortedTeamFixtures = [...(Array.isArray(teamFixtures) ? teamFixtures : [])].sort((a, b) => {
      const aKickoff = parseFixtureKickoffMs(a);
      const bKickoff = parseFixtureKickoffMs(b);
      if (aKickoff === null && bKickoff === null) return 0;
      if (aKickoff === null) return 1;
      if (bKickoff === null) return -1;
      return aKickoff - bKickoff;
    });

    const sortedTournamentFixtures = [...(
      Array.isArray(allTournamentFixtures) && allTournamentFixtures.length > 0
        ? allTournamentFixtures
        : sortedTeamFixtures
    )].sort((a, b) => {
      const aKickoff = parseFixtureKickoffMs(a);
      const bKickoff = parseFixtureKickoffMs(b);
      if (aKickoff === null && bKickoff === null) return 0;
      if (aKickoff === null) return 1;
      if (bKickoff === null) return -1;
      return aKickoff - bKickoff;
    });

    const playedFixtures = sortedTournamentFixtures.filter((fixture) => isFixturePlayed(fixture));
    const matchRows = playedFixtures.length > 0
      ? playedFixtures
      : (sortedTournamentFixtures.length > 0 ? sortedTournamentFixtures : selectedGroupFixtures);
    setMatchesResultsData(matchRows);

    const knockoutFixtures = sortedTournamentFixtures.filter((fixture) => !isGroupStageName(fixture.stage));
    const nowMs = Date.now();
    const upcomingKnockout = knockoutFixtures.filter((fixture) => {
      const kickoffMs = parseFixtureKickoffMs(fixture);
      return kickoffMs === null || kickoffMs >= nowMs - 3 * 60 * 60 * 1000;
    });

    if (knockoutFixtures.length === 0) {
      const projectedStructure = buildTournamentStructureTemplate(selectedGroupLetter, groupStandings);
      const projectedNext = buildProjectedNextPhaseFixtures(selectedGroupLetter, groupStandings);
      setTournamentStructureData(projectedStructure);
      setNextPhaseData(projectedNext.length > 0 ? projectedNext : (projectedStructure[0]?.fixtures || []).slice(0, 12));
      setNextPhaseLoading(false);
      setNextPhaseError('');
      setTeamViewError('');
      return;
    }

    const fixturesByStage = new Map();
    knockoutFixtures.forEach((fixture) => {
      const stageName = normalizeTournamentStageName(fixture.stage) || 'Knockout';
      const rows = fixturesByStage.get(stageName) || [];
      rows.push(fixture);
      fixturesByStage.set(stageName, rows);
    });

    const stageRows = Array.from(fixturesByStage.entries()).map(([stage, fixtures]) => ({
      stage,
      fixtures: [...fixtures].sort((a, b) => {
        const aKickoff = parseFixtureKickoffMs(a);
        const bKickoff = parseFixtureKickoffMs(b);
        if (aKickoff === null && bKickoff === null) return 0;
        if (aKickoff === null) return 1;
        if (bKickoff === null) return -1;
        return aKickoff - bKickoff;
      })
    }));

    setTournamentStructureData(stageRows);
    setNextPhaseData(upcomingKnockout.slice(0, 12));
    setNextPhaseLoading(false);
    setNextPhaseError('');
    setTeamViewError('');
  }, [allTournamentFixtures, groupStandings, selectedGroupFixtures, selectedGroupLetter, selectedTeam, showGroupStage, teamFixtures]);

  useEffect(() => {
    if (!showGroupStage) {
      setNextMatches([]);
      setAllTournamentFixtures([]);
      setNextMatchesLoading(false);
      setNextMatchesError('');
      setSelectedNextMatchId('');
      return;
    }

    let cancelled = false;

    async function loadUpcomingTournamentFixtures() {
      setNextMatchesLoading(true);
      setNextMatchesError('');

      try {
        const response = await fetch(`${API_BASE_URL}/fixtures/all`, {
          headers: buildAuthHeaders()
        });
        const json = await response.json();

        if (!response.ok) {
          throw new Error(json.error || json.message || 'Turnier-Spielplan konnte nicht geladen werden.');
        }

        const nowMs = Date.now();
        const fixtures = enrichFixtureTeams(Array.isArray(json.data) ? json.data : []);
        if (!cancelled) {
          setAllTournamentFixtures(fixtures);
        }
        const upcomingFixtures = fixtures
          .filter((fixture) => {
            const kickoffMs = parseFixtureKickoffMs(fixture);
            return kickoffMs !== null && kickoffMs >= nowMs;
          })
          .sort((a, b) => {
            const aKickoff = parseFixtureKickoffMs(a);
            const bKickoff = parseFixtureKickoffMs(b);
            if (aKickoff === null && bKickoff === null) return 0;
            if (aKickoff === null) return 1;
            if (bKickoff === null) return -1;
            return aKickoff - bKickoff;
          })
          .slice(0, NEXT_MATCHES_LIMIT);

        if (!cancelled) {
          setNextMatches(upcomingFixtures);
        }
      } catch (loadError) {
        if (!cancelled) {
          setNextMatches([]);
          setAllTournamentFixtures([]);
          setNextMatchesError(loadError instanceof Error ? loadError.message : 'Turnier-Spielplan konnte nicht geladen werden.');
        }
      } finally {
        if (!cancelled) {
          setNextMatchesLoading(false);
        }
      }
    }

    loadUpcomingTournamentFixtures();

    return () => {
      cancelled = true;
    };
  }, [showGroupStage, token, tenantSlug]);

  const selectedNextMatch = useMemo(() => {
    if (!selectedNextMatchId) {
      return null;
    }
    return nextMatches.find((fixture) => String(fixture.id) === String(selectedNextMatchId)) || null;
  }, [nextMatches, selectedNextMatchId]);

  function selectNextMatchForTip(fixtureId, focusTipForm = false) {
    nextMatchFocusPendingRef.current = Boolean(focusTipForm);
    setSelectedNextMatchId(String(fixtureId || ''));
  }

  useEffect(() => {
    if (nextMatches.length === 0) {
      setSelectedNextMatchId('');
      return;
    }

    const stillExists = nextMatches.some((fixture) => String(fixture.id) === String(selectedNextMatchId));
    if (!stillExists) {
      selectNextMatchForTip(nextMatches[0].id || '', false);
    }
  }, [nextMatches, selectedNextMatchId]);

  useEffect(() => {
    if (!selectedNextMatch || !nextMatchFocusPendingRef.current) {
      return;
    }

    nextMatchFocusPendingRef.current = false;

    if (nextMatchTipBlockRef.current) {
      nextMatchTipBlockRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    window.setTimeout(() => {
      if (nextMatchFriendSelectRef.current) {
        nextMatchFriendSelectRef.current.focus({ preventScroll: true });
      }
    }, 180);
  }, [selectedNextMatch]);

  useEffect(() => {
    if (!showGroupStage || !token) {
      setChatMessages([]);
      return;
    }

    let cancelled = false;

    async function loadChatMessages() {
      try {
        const response = await fetch(`${API_BASE_URL}/chat`, {
          headers: buildAuthHeaders()
        });
        if (response.ok) {
          const json = await response.json();
          if (!cancelled && Array.isArray(json.data)) {
            setChatMessages(json.data);
          }
        }
      } catch {
        // silently ignore poll errors
      }
    }

    loadChatMessages();
    const chatInterval = setInterval(loadChatMessages, 5000);

    return () => {
      cancelled = true;
      clearInterval(chatInterval);
    };
  }, [showGroupStage, token, tenantSlug]);

  async function sendChatMessage(event) {
    event.preventDefault();
    const text = chatInput.trim();
    if (!text || chatSending) {
      return;
    }

    setChatSending(true);
    try {
      const response = await fetch(`${API_BASE_URL}/chat`, {
        method: 'POST',
        headers: {
          ...buildAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text })
      });
      if (response.ok) {
        setChatInput('');
        const json = await response.json();
        if (json.data) {
          setChatMessages((prev) => [...prev, json.data]);
          setTimeout(() => {
            chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
          }, 50);
        }
      }
    } catch {
      // silently ignore
    } finally {
      setChatSending(false);
    }
  }

  const tournamentBracketStages = useMemo(() => {
    const orderMap = {
      'Group Stage': 1,
      'Play-offs': 2,
      'Round of 32': 3,
      'Round of 16': 4,
      'Quarter-finals': 5,
      'Semi-finals': 6,
      '3rd Place Final': 7,
      'Final': 8
    };

    return [...tournamentStructureData].sort((a, b) => {
      const aName = normalizeTournamentStageName(a?.stage);
      const bName = normalizeTournamentStageName(b?.stage);
      const aOrder = orderMap[aName] ?? 999;
      const bOrder = orderMap[bName] ?? 999;
      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }
      return aName.localeCompare(bName);
    });
  }, [tournamentStructureData]);

  const tournamentHierarchy = useMemo(() => {
    const stageMap = new Map(
      tournamentBracketStages.map((stage) => [normalizeTournamentStageName(stage.stage), {
        ...stage,
        stage: normalizeTournamentStageName(stage.stage)
      }])
    );

    const hierarchyOrder = WM_2026_PHASE_TEMPLATE.map((entry) => entry.stage);
    const orderedKnownStages = hierarchyOrder.filter((stageName) => stageMap.has(stageName));
    const unknownStages = tournamentBracketStages
      .map((stage) => normalizeTournamentStageName(stage.stage))
      .filter((stageName) => stageName && !hierarchyOrder.includes(stageName));

    const sequence = [...orderedKnownStages, ...unknownStages];

    return sequence.map((stageName) => {
      const stage = stageMap.get(stageName) || { stage: stageName, fixtures: [] };
      const fixtureCount = Array.isArray(stage.fixtures) ? stage.fixtures.length : 0;
      const templateMeta = WM_2026_PHASE_TEMPLATE.find((entry) => entry.stage === stageName);
      const teamsCount = fixtureCount > 0 ? fixtureCount * 2 : (templateMeta?.teamsCount || 0);
      return {
        ...stage,
        fixtureCount: fixtureCount > 0 ? fixtureCount : (templateMeta?.fixtureCount || 0),
        teamsCount
      };
    });
  }, [tournamentBracketStages]);

  const selectedDiagramStage = useMemo(
    () => tournamentHierarchy.find((stage) => stage.stage === selectedDiagramStageName) || null,
    [selectedDiagramStageName, tournamentHierarchy]
  );

  const selectedDiagramStageRows = useMemo(() => {
    if (!selectedDiagramStage || !Array.isArray(selectedDiagramStage.fixtures)) {
      return [];
    }

    return selectedDiagramStage.fixtures.map((fixture, index) => {
      const fixtureId = resolveFixtureIdValue(fixture) || `phase-${selectedDiagramStage.stage}-${index}`;
      return {
        fixture,
        fixtureId,
        tips: loadTipsForFixture(fixture)
      };
    });
  }, [selectedDiagramStage, tips]);

  const normalizedSelectedStageName = useMemo(
    () => normalizeTournamentStageName(selectedDiagramStageName),
    [selectedDiagramStageName]
  );

  const isSelectedKnockoutPhase = useMemo(() => {
    return Boolean(normalizedSelectedStageName && normalizedSelectedStageName !== 'Group Stage');
  }, [normalizedSelectedStageName]);

  const selectedPhaseFixtures = useMemo(() => {
    if (!isSelectedKnockoutPhase || selectedDiagramStageRows.length === 0) {
      return [];
    }
    return selectedDiagramStageRows.map((row) => row.fixture);
  }, [isSelectedKnockoutPhase, selectedDiagramStageRows]);

  const mainFixtures = useMemo(() => {
    return isSelectedKnockoutPhase ? selectedPhaseFixtures : selectedGroupFixtures;
  }, [isSelectedKnockoutPhase, selectedGroupFixtures, selectedPhaseFixtures]);

  useEffect(() => {
    if (tournamentHierarchy.length === 0) {
      if (selectedDiagramStageName) {
        setSelectedDiagramStageName('');
      }
      if (isPhaseViewActive) {
        setIsPhaseViewActive(false);
      }
      return;
    }

    const hasSelection = tournamentHierarchy.some((stage) => stage.stage === selectedDiagramStageName);
    if (hasSelection) {
      return;
    }

    const preferred = tournamentHierarchy.find((stage) => Array.isArray(stage.fixtures) && stage.fixtures.length > 0)
      || tournamentHierarchy[0];
    setSelectedDiagramStageName(preferred.stage);
  }, [isPhaseViewActive, selectedDiagramStageName, tournamentHierarchy]);

  useEffect(() => {
    if (!isDiagramModalOpen || !selectedDiagramStage) {
      return;
    }

    function closeOnEscape(event) {
      if (event.key === 'Escape') {
        setIsDiagramModalOpen(false);
      }
    }

    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [isDiagramModalOpen, selectedDiagramStage]);

  useEffect(() => {
    setIsPhaseViewActive(false);
  }, [selectedTeamId]);

  const latestFormation = useMemo(() => {
    if (!formationData.length) {
      return '';
    }
    return String(formationData[0].formation || '').trim();
  }, [formationData]);

  const formationRows = useMemo(() => {
    const parts = latestFormation
      .split('-')
      .map((item) => Number(item))
      .filter((n) => !Number.isNaN(n) && n > 0);

    const defendersCount = parts[0] || 4;
    const midfieldCount = parts[1] || 3;
    const attackersCount = parts[2] || 3;

    const players = Array.isArray(squadData?.players) ? squadData.players : [];
    const goalkeepers = players.filter((p) => /goalkeeper|\bgk\b/i.test(String(p.position || '')));
    const defenders = players.filter((p) => /defender|\bdef\b/i.test(String(p.position || '')));
    const midfielders = players.filter((p) => /midfielder|\bmid\b/i.test(String(p.position || '')));
    const attackers = players.filter((p) => /attacker|forward|striker|winger|\batt\b/i.test(String(p.position || '')));

    const gk = goalkeepers.slice(0, 1);
    const defRow = defenders.slice(0, defendersCount);
    const midRow = midfielders.slice(0, midfieldCount);
    const attRow = attackers.slice(0, attackersCount);

    return [attRow, midRow, defRow, gk].filter((row) => row.length > 0);
  }, [latestFormation, squadData]);

  function shortPlayerName(name) {
    const value = String(name || '').trim();
    if (!value) {
      return 'Player';
    }
    const parts = value.split(/\s+/).filter(Boolean);
    return parts.length > 1 ? parts[parts.length - 1] : parts[0];
  }

  useEffect(() => {
    if (!showGroupStage || !selectedTeam) {
      return;
    }

    let cancelled = false;

    async function loadTeamFixtures() {
      setFixturesLoading(true);
      setFixturesError('');

      try {
        const response = await fetch(`${API_BASE_URL}/fixtures/${selectedTeam.id}`, {
          headers: buildAuthHeaders()
        });
        const json = await response.json();

        if (!response.ok) {
          throw new Error(json.error || json.message || 'Fixtures konnten nicht geladen werden.');
        }

        const enriched = enrichFixtureTeams(json.data || []);
        const selectedTeamScore = selectedTeamsScoreFromFixtures(enriched, [selectedTeam.id]);

        if (
          currentWorkspaceRole === 'owner' &&
          showRoarPanel &&
          lastTeamScoreRef.current !== null &&
          selectedTeamScore > lastTeamScoreRef.current
        ) {
          playRoar();
        }
        lastTeamScoreRef.current = selectedTeamScore;

        if (!cancelled) {
          setTeamFixtures(enriched);
          setRssItems(buildLiveTickerItems(enriched, selectedTeam.name));
          setRssError('');
        }
      } catch (loadError) {
        if (!cancelled) {
          setTeamFixtures([]);
          setFixturesError(loadError instanceof Error ? loadError.message : 'Fixtures konnten nicht geladen werden.');
          setRssItems([]);
          setRssError('Live-Ticker konnte nicht geladen werden.');
        }
      } finally {
        if (!cancelled) {
          setFixturesLoading(false);
        }
      }
    }

    async function loadFriendsAndTips() {
      if (!token || !tenantSlug) {
        if (!cancelled) {
          setFriends([]);
          setTips([]);
        }
        return;
      }

      try {
        const friendsRes = await fetch(`${API_BASE_URL}/friends/${encodeURIComponent(FRIENDS_SCOPE_KEY)}`, {
          headers: buildAuthHeaders()
        });

        const friendsJson = await friendsRes.json();
        const friendRows = Array.isArray(friendsJson) ? friendsJson : [];

        if (!cancelled) {
          if (friendsRes.ok) {
            setFriends(friendRows);
            setTips(flattenFriendTips(friendRows));
          } else {
            setFriends([]);
            setTips([]);
          }
        }
      } catch {
        if (!cancelled) {
          setFriends([]);
          setTips([]);
        }
      }
    }

    async function loadSquad() {
      try {
        setSquadLoading(true);
        setSquadError('');
        const response = await fetch(`${API_BASE_URL}/players/${selectedTeam.id}`, {
          headers: buildAuthHeaders()
        });
        const json = await response.json();
        if (!response.ok) {
          throw new Error(json.error || json.message || 'Squad konnte nicht geladen werden.');
        }
        if (!cancelled) {
          setSquadData({
            coach: json.trainer ? { name: json.trainer } : null,
            players: Array.isArray(json.data) ? json.data : []
          });
        }
      } catch (squadLoadError) {
        if (!cancelled) {
          setSquadData({ coach: null, players: [] });
          setSquadError(squadLoadError instanceof Error ? squadLoadError.message : 'Squad konnte nicht geladen werden.');
        }
      } finally {
        if (!cancelled) {
          setSquadLoading(false);
        }
      }
    }

    async function loadAllGroupStageFixtures() {
      try {
        const response = await fetch(`${API_BASE_URL}/fixtures/group-stage/all`, {
          headers: buildAuthHeaders()
        });
        const json = await response.json();
        if (response.ok && Array.isArray(json.data) && json.data.length > 0) {
          if (!cancelled) {
            setAllGroupStageFixtures(enrichFixtureTeams(json.data));
          }
        }
      } catch {
        // silently ignore — teamFixtures remain as fallback
      }
    }

    async function loadTeamFormation() {
      try {
        const response = await fetch(`${API_BASE_URL}/fixtures/formation/${selectedTeam.id}`, {
          headers: buildAuthHeaders()
        });
        const json = await response.json();
        if (response.ok && Array.isArray(json.data)) {
          if (!cancelled) {
            setFormationData(json.data);
          }
          return;
        }
      } catch {
        // silently ignore and keep fallback behaviour
      }

      if (!cancelled) {
        setFormationData([]);
      }
    }

    loadTeamFixtures();
    loadFriendsAndTips();
    loadSquad();
    loadAllGroupStageFixtures();
    loadTeamFormation();

    const intervalId = setInterval(() => {
      loadTeamFixtures();
      loadAllGroupStageFixtures();
      loadTeamFormation();
    }, 60000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [currentWorkspaceRole, selectedTeam, showGroupStage, showRoarPanel, tenantSlug, token]);

  async function addFriend(event) {
    event.preventDefault();
    const trimmedName = friendName.trim();

    if (!trimmedName) {
      return;
    }

    try {
      if (!tenantSlug) {
        throw new Error('Workspace-Kontext fehlt. Bitte neu anmelden und einen Gruppen-Workspace auswaehlen.');
      }

      const addFriendResponse = await fetch(`${API_BASE_URL}/friends/${encodeURIComponent(FRIENDS_SCOPE_KEY)}/manual`, {
        method: 'POST',
        headers: {
          ...buildAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: trimmedName })
      });

      const addFriendJson = await parseJsonResponse(addFriendResponse);

      if (!addFriendResponse.ok) {
        const addMessage = addFriendJson.error || addFriendJson.message || '';
        throw new Error(addMessage || 'Freund konnte nicht hinzugefuegt werden.');
      }

      setFriendName('');
      setFixturesError('');

      const refreshRes = await fetch(`${API_BASE_URL}/friends/${encodeURIComponent(FRIENDS_SCOPE_KEY)}`, {
        headers: buildAuthHeaders()
      });
      const refreshJson = await parseJsonResponse(refreshRes);
      if (refreshRes.ok) {
        const friendRows = Array.isArray(refreshJson) ? refreshJson : [];
        setFriends(friendRows);
        setTips(flattenFriendTips(friendRows));
      }
    } catch (addError) {
      setFixturesError(addError instanceof Error ? addError.message : 'Freund konnte nicht hinzugefuegt werden.');
    }
  }

  function isManualFriendId(friendId) {
    return String(friendId || '').startsWith('manual-');
  }

  async function refreshFriendsAndTips() {
    const refreshRes = await fetch(`${API_BASE_URL}/friends/${encodeURIComponent(FRIENDS_SCOPE_KEY)}`, {
      headers: buildAuthHeaders()
    });
    const refreshJson = await parseJsonResponse(refreshRes);
    if (!refreshRes.ok) {
      throw new Error(refreshJson.error || refreshJson.message || 'Freundesliste konnte nicht aktualisiert werden.');
    }
    const friendRows = Array.isArray(refreshJson) ? refreshJson : [];
    setFriends(friendRows);
    setTips(flattenFriendTips(friendRows));
  }

  async function renameFriend(friendId, currentName = 'Freund') {
    if (!isManualFriendId(friendId)) {
      setFixturesError(`${currentName} ist ein Workspace-Benutzer und kann hier nicht umbenannt werden.`);
      return;
    }

    const nextNameRaw = globalThis.prompt('Neuen Namen eingeben', currentName);
    if (nextNameRaw === null) {
      return;
    }

    const nextName = String(nextNameRaw).trim().slice(0, 24);
    if (!nextName) {
      setFixturesError('Name darf nicht leer sein.');
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/friends/${encodeURIComponent(FRIENDS_SCOPE_KEY)}/manual/${encodeURIComponent(friendId)}`,
        {
          method: 'PATCH',
          headers: {
            ...buildAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ name: nextName })
        }
      );

      const json = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(json.error || json.message || 'Name konnte nicht geaendert werden.');
      }

      await refreshFriendsAndTips();
      setFixturesError('');
    } catch (renameError) {
      setFixturesError(renameError instanceof Error ? renameError.message : 'Name konnte nicht geaendert werden.');
    }
  }

  async function removeFriend(friendId, friendName = 'diesen Freund') {
    if (!isManualFriendId(friendId)) {
      setFixturesError(`${friendName} ist ein Workspace-Benutzer und kann hier nicht entfernt werden.`);
      return;
    }

    const confirmed = globalThis.confirm(`${friendName} wirklich entfernen?`);
    if (!confirmed) {
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/friends/${encodeURIComponent(FRIENDS_SCOPE_KEY)}/manual/${encodeURIComponent(friendId)}`,
        {
          method: 'DELETE',
          headers: buildAuthHeaders()
        }
      );
      const json = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(json.error || json.message || 'Freund konnte nicht entfernt werden.');
      }

      await refreshFriendsAndTips();
      setFixturesError('');
    } catch (removeError) {
      setFixturesError(removeError instanceof Error ? removeError.message : 'Freund konnte nicht entfernt werden.');
    }
  }

  async function resetGroupPassword() {
    setResetPasswordLoading(true);
    setResetPasswordError('');
    try {
      const response = await fetch(`${API_BASE_URL}/auth/reset-group-password`, {
        method: 'POST',
        headers: buildAuthHeaders(),
      });
      const json = await response.json();
      if (!response.ok) {
        throw new Error(json.error || json.message || 'Passwort konnte nicht zurueckgesetzt werden.');
      }
      if (json.generatedCredentials) {
        setWorkspaceLogin(json.generatedCredentials);
      }
    } catch (resetError) {
      setResetPasswordError(resetError instanceof Error ? resetError.message : 'Fehler beim Zuruecksetzen.');
    } finally {
      setResetPasswordLoading(false);
    }
  }

  async function copyWorkspaceLogin() {
    if (!workspaceLogin?.username || !workspaceLogin?.password || !globalThis.navigator?.clipboard) {
      return;
    }

    const groupName = tenantSlug || workspaceLogin.username;
    const line = [
      `Einladung zur Tippspiel-Gruppe ${groupName}`,
      `Login: ${workspaceLogin.username}`,
      `Passwort: ${workspaceLogin.password}`,
      'Einfach mit diesen Daten einloggen und weitere Freunde einladen.'
    ].join('\n');

    try {
      await globalThis.navigator.clipboard.writeText(line);
      setWorkspaceLoginCopied(true);
      globalThis.setTimeout(() => setWorkspaceLoginCopied(false), 1800);
    } catch {
      setWorkspaceLoginCopied(false);
    }
  }

  async function saveTip(event, fixtureId) {
    event.preventDefault();
    const draft = getTipDraft(fixtureId);
    if (!draft.friendId || draft.homeTip === '' || draft.awayTip === '') {
      return;
    }

    if (!selectedTeam) {
      return;
    }

    if (!tenantSlug) {
      setFixturesError('Workspace-Kontext fehlt fuer diesen Tipp.');
      return;
    }

    try {
      const nextTipsPayload = buildCurrentUserTipsPayload(tips, draft.friendId);
      nextTipsPayload[String(fixtureId)] = {
        home: String(draft.homeTip),
        away: String(draft.awayTip)
      };

      const response = await fetch(`${API_BASE_URL}/friends/${encodeURIComponent(FRIENDS_SCOPE_KEY)}`, {
        method: 'POST',
        headers: {
          ...buildAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ friendId: draft.friendId, tips: nextTipsPayload })
      });

      const json = await response.json();
      if (!response.ok) {
        throw new Error(json.error || json.message || 'Tipp konnte nicht gespeichert werden.');
      }

      const refreshRes = await fetch(`${API_BASE_URL}/friends/${encodeURIComponent(FRIENDS_SCOPE_KEY)}`, {
        headers: buildAuthHeaders()
      });
      const refreshJson = await refreshRes.json();
      if (refreshRes.ok) {
        const friendRows = Array.isArray(refreshJson) ? refreshJson : [];
        setFriends(friendRows);
        setTips(flattenFriendTips(friendRows));
      }
    } catch (saveError) {
      setFixturesError(saveError instanceof Error ? saveError.message : 'Tipp konnte nicht gespeichert werden.');
    }
  }

  return (
    <div className="app-shell">
      <div className="noise-layer" />
      <main className={user ? 'auth-card auth-card-wide' : 'auth-card'}>
        <div className="brand-row">
          <div className="brand-top-row">
            <span className="brand-pill">Grenzfall Multiuser</span>
            <div className="language-switch-wrap">
              <span className="language-flag" aria-hidden="true">{LANGUAGE_FLAG[language] || '🌐'}</span>
              <select
                className="language-select"
                value={languageSelectorValue}
                onChange={(event) => setLanguage(event.target.value === '🇬🇧' ? 'en' : 'de')}
                aria-label="Language"
              >
                <option value="🇩🇪">🇩🇪</option>
                <option value="🇬🇧">🇬🇧</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <h1 style={{ margin: 0 }}>Tippspiel Workspace</h1>
            {user ? (
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                {showGroupStage && selectedTeam ? (
                  <>
                    {currentWorkspaceRole === 'owner' ? (
                      <label className={showRoarPanel ? 'roar-toggle roar-toggle-active' : 'roar-toggle'}>
                        <span className="roar-toggle-label">{text.roarToggle}</span>
                        <input
                          type="checkbox"
                          checked={showRoarPanel}
                          onChange={(event) => { setShowRoarPanel(event.target.checked); }}
                          aria-label="ROOAR umschalten"
                        />
                        <span className="roar-toggle-track" aria-hidden="true">
                          <span className="roar-toggle-thumb" />
                        </span>
                      </label>
                    ) : null}
                    <button className="outline-btn" type="button" onClick={() => downloadIcs(selectedGroupFixtures, selectedTeam)}>.ics</button>
                    <button
                      className="outline-btn"
                      type="button"
                      onClick={() => {
                        if (!selectedGroupFixtures.length) return;
                        window.open(buildGoogleCalendarUrl(selectedGroupFixtures[0], selectedTeam), '_blank', 'noopener,noreferrer');
                      }}
                    >{text.gmailCalendar}</button>
                    <button className="outline-btn" type="button" onClick={() => setShowGroupStage(false)}>{text.switchTeam}</button>
                  </>
                ) : null}
                <button className="outline-btn" onClick={logout}>{text.logout}</button>
              </div>
            ) : null}
          </div>
          <p>{text.appSubtitle}</p>
          <section className="ticker-wrap">
            <div className="ticker-label">{text.tickerLabel}</div>
            <div className="ticker-track">
              <div className="ticker-content">{tickerText}</div>
            </div>
            {rssError ? <p className="inline-error">{rssError}</p> : null}
          </section>
                            <section className="side-card">
                              <button
                                type="button"
                                className="side-card-toggle"
                                onClick={() => setShowTeamFormationPanel((prev) => !prev)}
                                aria-expanded={showTeamFormationPanel}
                              >
                                <span>Team Formation</span>
                                <span className="side-card-toggle-icon">{showTeamFormationPanel ? '−' : '+'}</span>
                              </button>
                              {showTeamFormationPanel ? (
                                <div className="side-card-body">
                                  {formationRows.length > 0 ? (
                                    <div className="formation-card">
                                      <p className="formation-caption">TEAM FORMATION ({latestFormation || 'n/a'})</p>
                                      <div className="formation-pitch">
                                        {formationRows.map((row, rowIndex) => (
                                          <div className="formation-line" key={`line-${rowIndex}`}>
                                            {row.map((player) => (
                                              <span className="formation-pill" key={`fp-${player.id || `${player.name}-${rowIndex}`}`}>
                                                {shortPlayerName(player.name)}
                                              </span>
                                            ))}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ) : (
                                    <p className="tips-empty">Keine Formation vorhanden.</p>
                                  )}
                                </div>
                              ) : null}
                            </section>
        </div>

        {user ? (
          <section className="session-panel full-width">
            {workspaceLogin ? (
              <div className="inline-note" style={{ marginBottom: 12 }}>
                <button
                  type="button"
                  className="outline-btn"
                  onClick={copyWorkspaceLogin}
                  title="Einladung mit Login und Passwort kopieren"
                >
                  {workspaceLoginCopied ? 'Kopiert ✓' : 'Einladung kopieren'}
                </button>
              </div>
            ) : null}

            <section className="friends-admin friends-admin-inline">
              <h5>{text.addFriendsTitle}</h5>
              <form className="friend-form" onSubmit={addFriend}>
                <input
                  type="text"
                  placeholder={text.friendNamePlaceholder}
                  value={friendName}
                  onChange={(event) => setFriendName(event.target.value)}
                  required
                />
                <div className="friend-form-action">
                  <button type="submit" className="outline-btn">{text.addFriend}</button>
                </div>
              </form>
            </section>

            {showGroupStage && selectedTeam ? (
              <div className="group-stage-view">
                <div className="group-stage-headline">
                  {isSelectedKnockoutPhase ? (
                    <h3>{normalizedSelectedStageName}</h3>
                  ) : (
                    <h3>
                      {getFlagImageSrc(selectedTeam) ? (
                        <img className="inline-flag-img" src={getFlagImageSrc(selectedTeam)} alt={`${selectedTeam.name} Flagge`} loading="lazy" />
                      ) : (
                        <span className="flag">{selectedTeam.flag}</span>
                      )}
                      {' '}{selectedTeam.name} · Group {selectedGroupLetter}
                    </h3>
                  )}

                </div>

                {!isSelectedKnockoutPhase ? (
                  <div className="group-team-strip">
                    {selectedGroupTeams.map((team) => (
                      <span key={team.id} className={team.id === selectedTeam.id ? 'group-team-chip active' : 'group-team-chip'}>
                        {getFlagImageSrc(team) ? (
                          <img className="inline-flag-img" src={getFlagImageSrc(team)} alt={`${team.name} Flagge`} loading="lazy" />
                        ) : (
                          <span className="flag">{team.flag}</span>
                        )}
                        {' '}{team.name}
                      </span>
                    ))}
                  </div>
                ) : null}

                <div className="group-stage-content">
                  <aside className="phase-side-panel">
                    <section className="side-card">
                      <button
                        type="button"
                        className="side-card-toggle"
                        onClick={() => setShowSquadPanel((prev) => !prev)}
                        aria-expanded={showSquadPanel}
                      >
                        <span>{text.squadTitle}</span>
                        <span className="side-card-toggle-icon">{showSquadPanel ? '−' : '+'}</span>
                      </button>
                      {showSquadPanel ? (
                        <div className="side-card-body">
                          {squadLoading ? <p className="inline-note">{text.squadLoading}</p> : null}
                          {squadError ? <p className="inline-error">{squadError}</p> : null}
                          {!squadLoading && !squadError ? (
                            <>
                              <div className="squad-list-wrap">
                                {squadData?.coach ? (
                                  <div className="squad-row coach-row">
                                    <span className="squad-role">Trainer</span>
                                    <span className="squad-name">{squadData.coach.name}</span>
                                  </div>
                                ) : null}

                                {Array.isArray(squadData?.players) && squadData.players.length > 0 ? (
                                  squadData.players.map((player) => (
                                    <div className="squad-row" key={player.id || `${player.name}-${player.position}`}>
                                      <span className="squad-role">{player.position}</span>
                                      <span className="squad-name">{player.name}</span>
                                    </div>
                                  ))
                                ) : (
                                  <p className="tips-empty">{text.noSquadData}</p>
                                )}
                              </div>

                              <div className="squad-formation-block">
                                <h4>Formation</h4>
                                {formationRows.length > 0 ? (
                                  <div className="formation-card">
                                    <p className="formation-caption">TEAM FORMATION ({latestFormation || 'n/a'})</p>
                                    <div className="formation-pitch">
                                      {formationRows.map((row, rowIndex) => (
                                        <div className="formation-line" key={`line-${rowIndex}`}>
                                          {row.map((player) => (
                                            <span className="formation-pill" key={`fp-${player.id || `${player.name}-${rowIndex}`}`}>
                                              {shortPlayerName(player.name)}
                                            </span>
                                          ))}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ) : (
                                  <p className="tips-empty">{text.noFormationData}</p>
                                )}
                              </div>
                            </>
                          ) : null}
                        </div>
                      ) : null}
                    </section>

                    <section className="side-card">
                      <button
                        type="button"
                        className="side-card-toggle"
                        onClick={() => setShowNextMatchesPanel((prev) => !prev)}
                        aria-expanded={showNextMatchesPanel}
                      >
                        <span>Nächsten Spiele - Hier Tippen</span>
                        <span className="side-card-toggle-icon">{showNextMatchesPanel ? '−' : '+'}</span>
                      </button>
                      {showNextMatchesPanel ? (
                        <div className="side-card-body">
                          {nextMatchesLoading ? <p className="inline-note">Lade FIFA-Spielplan...</p> : null}
                          {nextMatchesError ? <p className="inline-error">{nextMatchesError}</p> : null}
                          {!nextMatchesLoading && !nextMatchesError ? (
                            <p className="inline-note">Zeigt die nächsten {NEXT_MATCHES_LIMIT} Spiele. Spiel anklicken, Freund wählen, Tipp speichern.</p>
                          ) : null}
                          {!nextMatchesLoading && !nextMatchesError && nextMatches.length > 0 ? (
                            <div className="next-matches-list-wrap">
                              {nextMatches.map((fixture, idx) => (
                                <button
                                  type="button"
                                  className={
                                    String(fixture.id) === String(selectedNextMatchId)
                                      ? 'next-match-card next-match-card-selected'
                                      : 'next-match-card'
                                  }
                                  key={`next-match-${fixture.id || idx}`}
                                  onClick={() => selectNextMatchForTip(fixture.id || '', true)}
                                >
                                  <div className="next-match-date">
                                    {formatClientKickoff(fixture.kickoffUtc)}
                                  </div>
                                  <div className="next-match-teams">
                                    <div className="next-match-team home">
                                      <span className="match-team-flag">
                                        {getFlagImageSrc(fixture.homeTeam) ? (
                                          <img className="inline-flag-img" src={getFlagImageSrc(fixture.homeTeam)} alt={`${fixture.homeTeam?.name || 'Home'} Flagge`} loading="lazy" />
                                        ) : (
                                          <span className="flag">{fixture.homeTeam?.flag || '🏳️'}</span>
                                        )}
                                      </span>
                                      <span className="match-team-name">{fixture.homeTeam?.name || 'TBD'}</span>
                                    </div>
                                    <div className="next-match-separator">vs</div>
                                    <div className="next-match-team away">
                                      <span className="match-team-name">{fixture.awayTeam?.name || 'TBD'}</span>
                                      <span className="match-team-flag">
                                        {getFlagImageSrc(fixture.awayTeam) ? (
                                          <img className="inline-flag-img" src={getFlagImageSrc(fixture.awayTeam)} alt={`${fixture.awayTeam?.name || 'Away'} Flagge`} loading="lazy" />
                                        ) : (
                                          <span className="flag">{fixture.awayTeam?.flag || '🏳️'}</span>
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                </button>
                              ))}
                            </div>
                          ) : null}
                          {!nextMatchesLoading && !nextMatchesError && nextMatches.length === 0 ? (
                            <p className="tips-empty">Keine anstehenden Spiele vorhanden.</p>
                          ) : null}
                          {!nextMatchesLoading && !nextMatchesError && selectedNextMatch ? (
                            <section className="tips-block next-match-tip-block" ref={nextMatchTipBlockRef}>
                              <div className="tips-headline">
                                <h5>Tipp fur: {selectedNextMatch.homeTeam?.name || 'TBD'} vs {selectedNextMatch.awayTeam?.name || 'TBD'}</h5>
                              </div>

                              <div className="tips-list">
                                {loadTipsForFixture(selectedNextMatch.id).length === 0 ? (
                                  <p className="tips-empty">Noch keine Tipps fur dieses Spiel.</p>
                                ) : (
                                  loadTipsForFixture(selectedNextMatch.id).slice(0, 10).map((tip) => (
                                    <div key={`next-tip-${tip.id}`} className="tip-item">
                                      <span className="tip-name">{tip.friend_name}</span>
                                      <strong className="tip-score">{tip.home_tip} : {tip.away_tip}</strong>
                                      <span className="tip-points">{calculateTipPoints(tip, selectedNextMatch)} P</span>
                                    </div>
                                  ))
                                )}
                              </div>

                              <form className="tip-form" onSubmit={(event) => saveTip(event, selectedNextMatch.id)}>
                                <select
                                  ref={nextMatchFriendSelectRef}
                                  value={getTipDraft(selectedNextMatch.id).friendId}
                                  onChange={(event) => setTipDraft(selectedNextMatch.id, { friendId: event.target.value })}
                                >
                                  <option value="">Freund wählen</option>
                                  {friends.map((friend) => (
                                    <option key={`next-friend-${friend.id}`} value={friend.id}>{friend.name}</option>
                                  ))}
                                </select>
                                <input
                                  type="number"
                                  min="0"
                                  placeholder="Home"
                                  value={getTipDraft(selectedNextMatch.id).homeTip}
                                  onChange={(event) => setTipDraft(selectedNextMatch.id, { homeTip: event.target.value })}
                                />
                                <input
                                  type="number"
                                  min="0"
                                  placeholder="Away"
                                  value={getTipDraft(selectedNextMatch.id).awayTip}
                                  onChange={(event) => setTipDraft(selectedNextMatch.id, { awayTip: event.target.value })}
                                />
                                <button type="submit" className="outline-btn">Tipp speichern</button>
                              </form>
                            </section>
                          ) : null}
                        </div>
                      ) : null}
                    </section>

                    <section className="side-card">
                      <button
                        type="button"
                        className="side-card-toggle"
                        onClick={() => setShowNextPhasePanel((prev) => !prev)}
                        aria-expanded={showNextPhasePanel}
                      >
                        <span>Select phase (WM 2026)</span>
                        <span className="side-card-toggle-icon">{showNextPhasePanel ? '−' : '+'}</span>
                      </button>
                      {showNextPhasePanel ? (
                        <div className="side-card-body">
                          {nextPhaseLoading ? <p className="inline-note">Lade Phasen-Daten...</p> : null}
                          {nextPhaseError ? <p className="inline-error">{nextPhaseError}</p> : null}
                          {tournamentHierarchy.length > 0 ? (
                            <>
                              <label htmlFor="wm2026-phase-select" className="phase-select-label">Select phase</label>
                              <select
                                id="wm2026-phase-select"
                                className="phase-select"
                                value={selectedDiagramStageName}
                                onChange={(event) => {
                                  setSelectedDiagramStageName(event.target.value);
                                  setIsPhaseViewActive(true);
                                }}
                              >
                                {tournamentHierarchy.map((stage) => (
                                  <option key={`wm-phase-${stage.stage}`} value={stage.stage}>
                                    {stage.stage}
                                  </option>
                                ))}
                              </select>

                              {selectedDiagramStage ? (
                                selectedDiagramStageRows.length > 0 ? (
                                  <div className="next-phase-table-wrap">
                                    <table className="next-phase-table">
                                      <thead>
                                        <tr>
                                          <th>Match</th>
                                          <th>Result</th>
                                          <th>Friend tips</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {selectedDiagramStageRows.map(({ fixture, fixtureId, tips: fixtureTips }) => {
                                          const homeTeamMeta = getCanonicalTeamMeta(fixture.homeTeam?.id, fixture.homeTeam?.name);
                                          const awayTeamMeta = getCanonicalTeamMeta(fixture.awayTeam?.id, fixture.awayTeam?.name);
                                          const homeFlagSrc = getFlagImageSrc(homeTeamMeta);
                                          const awayFlagSrc = getFlagImageSrc(awayTeamMeta);
                                          const homeTeamName = homeTeamMeta?.name || fixture.homeTeam?.name || 'TBD';
                                          const awayTeamName = awayTeamMeta?.name || fixture.awayTeam?.name || 'TBD';

                                          return (
                                          <tr key={`sel-phase-${selectedDiagramStage.stage}-${fixtureId}`}>
                                            <td>
                                              <span className="team-name-with-flag">
                                                {homeFlagSrc ? (
                                                  <img className="inline-flag-img" src={homeFlagSrc} alt={`${homeTeamName} Flagge`} loading="lazy" />
                                                ) : (
                                                  <span className="flag">{homeTeamMeta?.flag || fixture.homeTeam?.flag || '🏳️'}</span>
                                                )}
                                                {' '}{homeTeamName}
                                              </span>
                                              {' '}vs{' '}
                                              <span className="team-name-with-flag">
                                                {awayFlagSrc ? (
                                                  <img className="inline-flag-img" src={awayFlagSrc} alt={`${awayTeamName} Flagge`} loading="lazy" />
                                                ) : (
                                                  <span className="flag">{awayTeamMeta?.flag || fixture.awayTeam?.flag || '🏳️'}</span>
                                                )}
                                                {' '}{awayTeamName}
                                              </span>
                                            </td>
                                            <td>
                                              {isFixturePlayed(fixture)
                                                ? `${Number(fixture.homeScore || 0)} : ${Number(fixture.awayScore || 0)}`
                                                : 'Noch offen'}
                                            </td>
                                            <td>
                                              {fixtureTips.length > 0 ? (
                                                <details className="phase-tip-details">
                                                  <summary>{fixtureTips.length} Tipps</summary>
                                                  <div className="phase-tip-list">
                                                    {fixtureTips.map((tip) => (
                                                      <div key={`phase-tip-${fixtureId}-${tip.id}`} className="phase-tip-item">
                                                        <span>{tip.friend_name}</span>
                                                        <strong>{tip.home_tip} : {tip.away_tip}</strong>
                                                      </div>
                                                    ))}
                                                  </div>
                                                </details>
                                              ) : (
                                                <span className="tips-empty">Keine Friend-Tipps</span>
                                              )}
                                            </td>
                                          </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                ) : (
                                  <p className="tips-empty">Keine Spiele fur diese Phase vorhanden.</p>
                                )
                              ) : null}
                            </>
                          ) : (
                            <p className="tips-empty">Noch keine Phasen-Daten vorhanden.</p>
                          )}
                        </div>
                      ) : null}
                    </section>

                    <section className="side-card">
                      <button
                        type="button"
                        className="side-card-toggle"
                        onClick={() => setShowChatPanel((prev) => !prev)}
                        aria-expanded={showChatPanel}
                      >
                        <span>Gruppen-Chat</span>
                        <span className="side-card-toggle-icon">{showChatPanel ? '−' : '+'}</span>
                      </button>
                      {showChatPanel ? (
                        <div className="side-card-body">
                          <div className="chat-messages-wrap">
                            {chatMessages.length === 0 ? (
                              <p className="tips-empty">Noch keine Nachrichten. Schreib was!</p>
                            ) : (
                              chatMessages.map((msg) => {
                                const isOwn = msg.userId === user?.id;
                                return (
                                  <div key={msg.id} className={isOwn ? 'chat-message chat-message-own' : 'chat-message'}>
                                    <span className="chat-name">{isOwn ? 'Du' : msg.userName}</span>
                                    <span className="chat-text">{msg.text}</span>
                                    <span className="chat-time">{formatClientKickoff(msg.createdAt)}</span>
                                  </div>
                                );
                              })
                            )}
                            <div ref={chatBottomRef} />
                          </div>
                          <form className="chat-form" onSubmit={sendChatMessage}>
                            <input
                              type="text"
                              className="chat-input"
                              placeholder="Nachricht schreiben..."
                              value={chatInput}
                              onChange={(event) => setChatInput(event.target.value)}
                              maxLength={500}
                              disabled={chatSending}
                            />
                            <button type="submit" className="outline-btn" disabled={chatSending || !chatInput.trim()}>
                              {chatSending ? '...' : 'Senden'}
                            </button>
                          </form>
                        </div>
                      ) : null}
                    </section>

                    <section className="side-card points-table-card">
                      <h4>{text.pointsStatsTitle}</h4>
                      <p className="points-rules">{text.pointsRules}</p>
                      {friendPointsTable.length === 0 ? (
                        <p className="tips-empty">{text.noPoints}</p>
                      ) : (
                        <div className="points-table-wrap">
                          <table className="points-table">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Freund</th>
                                <th>Tipps</th>
                                <th>Exakt</th>
                                <th>{text.pointsCorrectTeam}</th>
                                <th>Punkte</th>
                                <th>Tipps & Spiele</th>
                                <th aria-label="Aktion">Aktion</th>
                              </tr>
                            </thead>
                            <tbody>
                              {friendPointsTable.map((row, index) => (
                                <tr key={`pts-${row.friendId}`}>
                                  <td>{index + 1}</td>
                                  <td>{row.friendName}</td>
                                  <td>{row.tipsCount}</td>
                                  <td>{row.exactHits}</td>
                                  <td>{row.correctTeamHits}</td>
                                  <td><strong>{row.points}</strong></td>
                                  <td>
                                    {row.tipDetails.length > 0 ? (
                                      <details className="points-tip-collapsible">
                                        <summary className="points-tip-summary">
                                          {row.tipDetails.length} Tipps anzeigen
                                        </summary>
                                        <div className="points-tip-list">
                                          {row.tipDetails.map((entry) => (
                                            <div key={`${row.friendId || row.friendName}-${entry.fixtureId}`} className="points-tip-item">
                                              <span className="points-tip-match">{entry.matchLabel}</span>
                                              <strong className="points-tip-score">{entry.tipScore}</strong>
                                            </div>
                                          ))}
                                        </div>
                                      </details>
                                    ) : (
                                      <span className="tips-empty">Keine Tipps</span>
                                    )}
                                  </td>
                                  <td>
                                    {row.friendId ? (
                                      <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                                        <button
                                          type="button"
                                          className="outline-btn"
                                          onClick={() => renameFriend(row.friendId, row.friendName)}
                                          aria-label={`${row.friendName} umbenennen`}
                                          title={`${row.friendName} umbenennen`}
                                          style={{ padding: '2px 8px', minWidth: 0 }}
                                        >
                                          ✎
                                        </button>
                                        <button
                                          type="button"
                                          className="friend-remove-btn friend-remove-btn-small"
                                          onClick={() => removeFriend(row.friendId, row.friendName)}
                                          aria-label={`${row.friendName} entfernen`}
                                          title={`${row.friendName} entfernen`}
                                        >
                                          x
                                        </button>
                                      </div>
                                    ) : null}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </section>

                    <section className="side-card">
                      <h4>Group View {selectedGroupLetter ? `· Group ${selectedGroupLetter}` : ''}</h4>
                      {teamViewError ? <p className="inline-error">{teamViewError}</p> : null}
                      {groupStandings.length > 0 ? (
                        <div className="next-phase-table-wrap">
                          <table className="next-phase-table group-standings-table">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Team</th>
                                <th>Sp</th>
                                <th>S</th>
                                <th>U</th>
                                <th>N</th>
                                <th>Tore</th>
                                <th>Diff</th>
                                <th>Pkt</th>
                              </tr>
                            </thead>
                            <tbody>
                              {groupStandings.map((row) => (
                                <tr key={`gst-${row.teamId}`}>
                                  <td>{row.rank}</td>
                                  <td>
                                    {(() => {
                                      const canonicalTeam = getCanonicalTeamMeta(row.teamId, row.teamName);
                                      const flagSrc = getFlagImageSrc(canonicalTeam);
                                      return (
                                        <span className="team-name-with-flag">
                                          {flagSrc ? (
                                            <img
                                              className="inline-flag-img"
                                              src={flagSrc}
                                              alt={`${canonicalTeam.name} Flagge`}
                                              loading="lazy"
                                            />
                                          ) : (
                                            <span className="flag">{canonicalTeam.flag || '🏳️'}</span>
                                          )}
                                          {' '}{canonicalTeam.name}
                                        </span>
                                      );
                                    })()}
                                  </td>
                                  <td>{row.played}</td>
                                  <td>{row.won}</td>
                                  <td>{row.draw}</td>
                                  <td>{row.lost}</td>
                                  <td>{row.goalsFor}:{row.goalsAgainst}</td>
                                  <td>{row.goalDiff > 0 ? `+${row.goalDiff}` : row.goalDiff}</td>
                                  <td><strong>{row.points}</strong></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="tips-empty">Keine Group-View-Daten vorhanden.</p>
                      )}
                    </section>

                    <section className="side-card">
                      <h4>Matches & Results</h4>
                      {matchesResultsData.length > 0 ? (
                        <div className="next-phase-table-wrap">
                          <table className="next-phase-table">
                            <thead>
                              <tr>
                                <th>Stage</th>
                                <th>Match</th>
                                <th>Result</th>
                              </tr>
                            </thead>
                            <tbody>
                              {matchesResultsData.slice(-10).reverse().map((fixture) => (
                                <tr key={`mr-${fixture.id}`}>
                                  <td>{fixture.stage}</td>
                                  <td>{fixture.homeTeam?.name} vs {fixture.awayTeam?.name}</td>
                                  <td>{fixture.homeScore} : {fixture.awayScore}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="tips-empty">Keine Match/Result-Daten vorhanden.</p>
                      )}
                    </section>

                    <section className="side-card">
                      <button
                        type="button"
                        className="side-card-toggle"
                        onClick={() => setShowNextPhaseDetailsPanel((prev) => !prev)}
                        aria-expanded={showNextPhaseDetailsPanel}
                      >
                        <span>Phasen-Details</span>
                        <span className="side-card-toggle-icon">{showNextPhaseDetailsPanel ? '−' : '+'}</span>
                      </button>
                      {showNextPhaseDetailsPanel ? (
                        <div className="side-card-body">
                          {tournamentBracketStages.length > 0 ? (
                            <div className="bracket-scroll">
                              <div className="bracket-board">
                            {tournamentBracketStages.map((stage) => (
                              <div key={`tsd-${stage.stage}`} className="bracket-stage-col">
                                <h5>{stage.stage}</h5>
                                <div className="bracket-stage-matches">
                                  {(stage.fixtures || []).slice(0, 8).map((fixture) => (
                                    <article key={`tsdf-${fixture.id}`} className="bracket-match-card">
                                      <div className="bracket-team-row">
                                        <span>{fixture.homeTeam?.name}</span>
                                        <strong>{fixture.homeScore}</strong>
                                      </div>
                                      <div className="bracket-team-row">
                                        <span>{fixture.awayTeam?.name}</span>
                                        <strong>{fixture.awayScore}</strong>
                                      </div>
                                    </article>
                                  ))}
                                </div>
                              </div>
                            ))}
                              </div>
                            </div>
                          ) : (
                            <p className="tips-empty">Noch keine Detaildaten vorhanden.</p>
                          )}
                        </div>
                      ) : null}
                    </section>

                    <section className="side-card">
                      <h4>FIFA World Cup 2026</h4>
                      {tournamentHierarchy.length > 0 ? (
                        <div className="tournament-structure-container">
                          <div className="tournament-phases-grid">
                            {tournamentHierarchy.map((stage) => (
                              <button
                                key={`phase-${stage.stage}`}
                                type="button"
                                className={
                                  stage.stage === selectedDiagramStageName
                                    ? 'tournament-phase-box tournament-phase-box-active'
                                    : 'tournament-phase-box'
                                }
                                onClick={() => {
                                  setSelectedDiagramStageName(stage.stage);
                                  setIsPhaseViewActive(true);
                                  setIsDiagramModalOpen(true);
                                  setShowNextPhasePanel(true);
                                }}
                                title={`${stage.stage}: ${stage.teamsCount} Teams, ${stage.fixtureCount} Spiele`}
                              >
                                <div className="phase-box-title">{stage.stage}</div>
                                <div className="phase-box-teams">{stage.teamsCount} Teams</div>
                                <div className="phase-box-matches">{stage.fixtureCount} Spiele</div>
                              </button>
                            ))}
                          </div>
                          <div className="tournament-info-box">
                            <div className="info-section">
                              <strong>2026 Format:</strong>
                              <ul>
                                <li>🏟️ 16 Stadien in USA, Kanada, Mexiko</li>
                                <li>📅 Juni - Juli 2026</li>
                                <li>⚽ 80 Gesamtspiele</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <p className="tips-empty">Noch keine Tournament-Structure-Daten vorhanden.</p>
                      )}
                    </section>
                  </aside>

                <div className="fixture-list">
                  {fixturesLoading ? (
                    <p className="inline-note">
                      {isSelectedKnockoutPhase
                        ? `Lade ${normalizedSelectedStageName}-Spiele...`
                        : 'Lade Group-Stage-Fixtures von Sportmonks...'}
                    </p>
                  ) : null}
                  {!fixturesLoading && fixturesError ? (
                    <p className="inline-error">{fixturesError}</p>
                  ) : null}
                  {!fixturesLoading && !fixturesError && mainFixtures.length === 0 ? (
                    <p className="tips-empty">
                      {isSelectedKnockoutPhase
                        ? `Keine Spiele für ${normalizedSelectedStageName} vorhanden.`
                        : 'Keine Group-Stage-Spiele vorhanden.'}
                    </p>
                  ) : null}
                  {mainFixtures.map((fixture) => {
                    const fixtureResolvedId = resolveFixtureIdValue(fixture);
                    const fixtureTips = loadTipsForFixture(fixture);
                    const visibleTips = fixtureTips.slice(0, 10);
                    const homeTeamMeta = getCanonicalTeamMeta(fixture.homeTeam?.id, fixture.homeTeam?.name);
                    const awayTeamMeta = getCanonicalTeamMeta(fixture.awayTeam?.id, fixture.awayTeam?.name);
                    const homeFlagSrc = getFlagImageSrc(homeTeamMeta);
                    const awayFlagSrc = getFlagImageSrc(awayTeamMeta);
                    const homeTeamName = homeTeamMeta?.name || fixture.homeTeam?.name || 'Home';
                    const awayTeamName = awayTeamMeta?.name || fixture.awayTeam?.name || 'Away';
                    const venue = fixture.venue || {
                      name: 'Unknown venue',
                      city: 'Unknown city',
                      country: 'Unknown country',
                      timeZone: 'UTC',
                      image: ''
                    };

                    return (
                      <article key={fixtureResolvedId || `${fixture.stage || 'stage'}-${homeTeamName}-${awayTeamName}-${fixture.kickoffUtc || 'kickoff'}`} className="fixture-card">
                        <header>
                          <p className="fixture-stage">{fixture.stage}</p>
                        </header>

                        <div className="match-teams-line">
                          <span className="team-home">
                            {homeFlagSrc ? (
                              <img className="match-flag-img" src={homeFlagSrc} alt={`${homeTeamName} Flagge`} loading="lazy" />
                            ) : (
                              <span className="flag">{homeTeamMeta?.flag || fixture.homeTeam?.flag || '🏳️'}</span>
                            )}
                            {' '}{homeTeamName}
                          </span>
                          <span className="team-away">
                            {awayFlagSrc ? (
                              <img className="match-flag-img" src={awayFlagSrc} alt={`${awayTeamName} Flagge`} loading="lazy" />
                            ) : (
                              <span className="flag">{awayTeamMeta?.flag || fixture.awayTeam?.flag || '🏳️'}</span>
                            )}
                            {' '}{awayTeamName}
                          </span>
                        </div>

                        <div className="score-line">
                          <strong>{Number(fixture.homeScore || 0)} : {Number(fixture.awayScore || 0)}</strong>
                        </div>

                        <div className="fixture-lower">
                          <div className="fixture-meta-grid">
                            <div className="meta-col meta-col-left">
                              <div className="meta-item">
                                <span className="meta-label">Local time at venue</span>
                                <span>{formatVenueKickoff(fixture.kickoffUtc, venue.timeZone)}</span>
                                </div>
                                <div className="meta-item">
                                <span className="meta-label">Local time for you</span>
                                <span>{formatClientKickoff(fixture.kickoffUtc)}</span>
                              </div>
                            </div>

                            <div className="meta-col meta-col-right">
                              <div className="meta-item">
                                <span className="meta-label">Stadium</span>
                                <span className="venue-meta-with-thumb">
                                  <span className="venue-name-text">{venue.name}</span>
                                  <img
                                    className="venue-thumb"
                                    src={getVenueImageSrc(venue) || getVenueFallbackSvg(venue)}
                                    alt={`${venue.name} stadium`}
                                    loading="lazy"
                                    onError={(event) => {
                                      if (event.currentTarget.src.startsWith('data:image/svg+xml')) {
                                        return;
                                      }
                                      event.currentTarget.src = getVenueFallbackSvg(venue);
                                    }}
                                  />
                                </span>
                              </div>
                              <div className="meta-item">
                                <span className="meta-label">City</span>
                                <span>{venue.city}, {venue.country}</span>
                              </div>
                            </div>
                          </div>

                          <section className="tips-block">
                            <div className="tips-headline">
                              <h5>Tipps deiner Freunde</h5>
                              <span>{visibleTips.length}/10</span>
                            </div>
                            <div className="tips-list">
                              {fixtureTips.length === 0 ? (
                                <p className="tips-empty">Noch keine Tipps für dieses Spiel.</p>
                              ) : (
                                visibleTips.map((tip) => (
                                  <div key={tip.id} className="tip-item">
                                    <span className="tip-name">{tip.friend_name}</span>
                                    <strong className="tip-score">{tip.home_tip} : {tip.away_tip}</strong>
                                    <span className="tip-points">{calculateTipPoints(tip, fixture)} P</span>
                                  </div>
                                ))
                              )}
                            </div>
                            {fixtureTips.length > 10 ? <p className="tips-more">+{fixtureTips.length - 10} weitere Tipps gespeichert</p> : null}

                            <form className="tip-form" onSubmit={(event) => saveTip(event, fixtureResolvedId)}>
                              <select
                                value={getTipDraft(fixtureResolvedId).friendId}
                                onChange={(event) => setTipDraft(fixtureResolvedId, { friendId: event.target.value })}
                              >
                                <option value="">Freund wählen</option>
                                {friends.map((friend) => (
                                  <option key={friend.id} value={friend.id}>{friend.name}</option>
                                ))}
                              </select>
                              <input
                                type="number"
                                min="0"
                                placeholder="Home"
                                value={getTipDraft(fixtureResolvedId).homeTip}
                                onChange={(event) => setTipDraft(fixtureResolvedId, { homeTip: event.target.value })}
                              />
                              <input
                                type="number"
                                min="0"
                                placeholder="Away"
                                value={getTipDraft(fixtureResolvedId).awayTip}
                                onChange={(event) => setTipDraft(fixtureResolvedId, { awayTip: event.target.value })}
                              />
                              <button type="submit" className="outline-btn">Tipp speichern</button>
                            </form>
                          </section>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </div>
              {isDiagramModalOpen && selectedDiagramStage ? (
                <div
                  className="phase-diagram-overlay"
                  role="dialog"
                  aria-modal="true"
                  aria-label={`${selectedDiagramStage.stage} Diagramm`}
                  onClick={() => setIsDiagramModalOpen(false)}
                >
                  <div className="phase-diagram-modal" onClick={(event) => event.stopPropagation()}>
                    <div className="phase-diagram-head">
                      <div>
                        <h4>{selectedDiagramStage.stage}</h4>
                        <p>Schematisches Diagramm · {selectedDiagramStage.fixtures?.length || 0} Spiele</p>
                      </div>
                      <button
                        type="button"
                        className="friend-remove-btn"
                        onClick={() => setIsDiagramModalOpen(false)}
                        aria-label="Diagramm schliessen"
                        title="Schliessen"
                      >
                        x
                      </button>
                    </div>

                    <div className="phase-diagram-board">
                      {selectedDiagramStageRows.length > 0 ? (
                        selectedDiagramStageRows.map(({ fixture, tips: fixtureTips }, index) => (
                          <article key={`diag-${selectedDiagramStage.stage}-${fixture.id}`} className="phase-diagram-match">
                            <span className="phase-diagram-match-index">Match {index + 1}</span>
                            <div className="phase-diagram-team-row">
                              <span>{fixture.homeTeam?.name || 'TBD'}</span>
                              <strong>{Number(fixture.homeScore || 0)}</strong>
                            </div>
                            <div className="phase-diagram-team-row">
                              <span>{fixture.awayTeam?.name || 'TBD'}</span>
                              <strong>{Number(fixture.awayScore || 0)}</strong>
                            </div>
                            <div className="phase-diagram-tip-list">
                              {fixtureTips.length > 0 ? (
                                fixtureTips.map((tip) => (
                                  <div key={`diag-tip-${fixture.id}-${tip.id}`} className="phase-diagram-tip-item">
                                    <span>{tip.friend_name}</span>
                                    <strong>{tip.home_tip}:{tip.away_tip}</strong>
                                  </div>
                                ))
                              ) : (
                                <span className="tips-empty">Keine Friend-Tipps</span>
                              )}
                            </div>
                          </article>
                        ))
                      ) : (
                        <p className="tips-empty">Keine Spiele fur diese Phase vorhanden.</p>
                      )}
                    </div>
                  </div>
                </div>
              ) : null}
              </div>
            ) : (
              <>
                <input
                  className="team-search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Team oder Region suchen"
                />

                <div className="team-groups">
                  {groupedTeams.map(([letter, teams]) => (
                    <div className="group-row" key={letter}>
                      <div className="group-rail">{letter}</div>
                      <div className="group-grid">
                        {renderRows(teams).map((row, rowIndex) => (
                          <div className="team-row" key={`${letter}-${rowIndex}`}>
                            {row.map((team) => (
                              <button
                                key={team.id}
                                type="button"
                                className={team.id === selectedTeamId ? 'team-card selected' : 'team-card'}
                                onClick={() => chooseTeam(team.id)}
                              >
                                {getFlagImageSrc(team) ? (
                                  <img className="team-flag-img" src={getFlagImageSrc(team)} alt={`${team.name} Flagge`} loading="lazy" />
                                ) : (
                                  <span className="flag">{team.flag}</span>
                                )}
                                <span className="team-name">{team.name}</span>
                              </button>
                            ))}
                            {Array.from({ length: 4 - row.length }).map((_, fillIdx) => (
                              <div className="team-card spacer" key={`${letter}-${rowIndex}-fill-${fillIdx}`} />
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>
        ) : (
          <>
            <div className="mode-switch">
              <button
                className={mode === 'login' ? 'tab active' : 'tab'}
                onClick={() => setMode('login')}
                type="button"
              >
                {text.modeLogin}
              </button>
              <button
                className={mode === 'register' ? 'tab active' : 'tab'}
                onClick={() => setMode('register')}
                type="button"
              >
                {text.modeRegister}
              </button>
            </div>

            <form className="auth-form" onSubmit={submitForm}>
              <h2>{title}</h2>

              {mode === 'register' && (
                <label>
                  {text.labelName}
                  <input
                    type="text"
                    value={form.name}
                    onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                    required
                    placeholder={text.namePlaceholder}
                  />
                </label>
              )}

              {mode === 'register' && (
                <label>
                  {text.labelGroupName}
                  <input
                    type="text"
                    value={form.workspaceName}
                    onChange={(event) => setForm((prev) => ({ ...prev, workspaceName: event.target.value }))}
                    required
                    placeholder={text.groupPlaceholder}
                  />
                </label>
              )}

              {mode === 'login' && (
                <label>
                  {text.labelUsername}
                  <input
                    type="text"
                    value={form.username}
                    onChange={(event) => setForm((prev) => ({ ...prev, username: event.target.value }))}
                    required
                    placeholder={text.usernamePlaceholder}
                  />
                </label>
              )}

              {mode === 'login' && (
                <label>
                  {text.labelPassword}
                  <input
                    type="password"
                    value={form.password}
                    onChange={(event) => setForm((prev) => ({ ...prev, password: event.target.value }))}
                    required
                    minLength={6}
                    placeholder={text.passwordPlaceholder}
                  />
                </label>
              )}

              {mode === 'register' && (
                <p className="inline-note">
                  {text.registerHint}<strong>win2026%gruppenname</strong> + generiertes Passwort.
                </p>
              )}

              {error && <p className="error-message">{error}</p>}

              <button className="primary-btn" type="submit" disabled={loading}>
                {loading ? 'Bitte warten...' : title}
              </button>
            </form>
          </>
        )}
      </main>
    </div>
  );
}

export default App;
